import { useState, useMemo, useCallback, useEffect } from "react";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import ApprovalIcon from "@mui/icons-material/Approval";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileDownloadOffIcon from "@mui/icons-material/FileDownloadOff";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import {
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FiCopy, FiClock } from "react-icons/fi";
import { X, FileText, Clock } from "lucide-react";

import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";

import { useAuth } from "@components/context/AuthContext";
import Spinner from "@components/Spinner";
import { AgGridContextMenu } from "@components/ag_grid/AgGridContextMenu";
import { PayrollReviewFilters } from "@components/payroll_components/PayrollReviewFilters";
import axiosClient from "@service/axiosClient";
import {
  generateSelectedPaystubs,
  voidSelectedPaystubs,
  fetchChecksReview,
  generateChecks,
  cancelChecks,
  printChecks,
  validateDirectDeposit,
  fetchPaymentFiles,
  previewPaymentChecks,
  generatePaymentFile,
  previewAchSplit,
  generateAchSplit,
  exportPaymentFile,
  setPaystubEdit,
  getPaystubDifference,
  generateDifferencePaystub,
} from "@service/paystubApiService";
import {
  amountToWords,
  formatMicrLine,
  hasCompleteMicrData,
  micrRightOffsetIn,
} from "@utils/checkFormatting";

interface PayrollReviewRow {
  "B&B ID": string | number | null;
  "Pay Type": string | null;
  Date: string | null;
  Payee: string | null;
  Memo: string | null;
  Amount: number | null;
  "Gross Pay": number | null;
  Tax: number | null;
  Hours: number | null;
  "Pay Rate Value": number | string | null;
  "Rate Type": string | null;
  "Overtime Rate": number | null;
  Other: number | null;
  "Other Additions": number | null;
  "Admin Fee": number | string | null;
  Rent: number | null;
  Holiday: number | null;
  PTO: number | null;
  "Pay Period": string | null;
  "Check Number": string | number | null;
  "Cancel Reason": string | null;
  Status: string | null;
  Edit?: boolean | null;
  "Paystub ID"?: number | null;
}

interface PaystubDifferenceField {
  saved: any;
  calculated: any;
  delta: number | null;
}

interface PaystubDifferenceResponse {
  paystub_id: number;
  saved: Record<string, any> | null;
  calculated: Record<string, any>;
  diff: Record<string, PaystubDifferenceField>;
}

function getPreviousSunday(): string {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysSinceLastSunday = dayOfWeek + 7;
  const lastSunday = new Date(
    today.setDate(today.getDate() - daysSinceLastSunday)
  );
  lastSunday.setHours(0, 0, 0, 0);
  return formatDate(lastSunday);
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isSelectablePeriod(periodStart: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayOfWeek = today.getDay();

  // Current week's Sunday
  const currentWeekSunday = new Date(today);
  currentWeekSunday.setDate(today.getDate() - dayOfWeek);

  // Previous week's Sunday
  const previousWeekSunday = new Date(currentWeekSunday);
  previousWeekSunday.setDate(currentWeekSunday.getDate() - 7);

  const selected = new Date(periodStart + "T00:00:00");
  return selected.getTime() >= previousWeekSunday.getTime();
}

function getEndDateStr(startStr: string): string {
  const start = new Date(startStr + "T00:00:00");
  start.setDate(start.getDate() + 6);
  return formatDate(start);
}

const StatusCellRenderer = (props: {
  value: string;
  data?: PayrollReviewRow;
}) => {
  const isEditingFinalized =
    props.value === "Finalized" && props.data?.Edit === true;

  const getStatusClass = (status: string) => {
    if (isEditingFinalized) return "tw-bg-orange-100 tw-text-orange-800";
    if (status === "Review") return "tw-bg-yellow-100 tw-text-yellow-800";
    if (status === "Finalized") return "tw-bg-green-100 tw-text-green-800";
    if (status === "Missing") return "tw-bg-red-100 tw-text-red-800";
    if (status === "Zero Hours") return "tw-bg-blue-100 tw-text-blue-800";
    return "";
  };

  const displayValue = isEditingFinalized ? `Editing` : props.value;

  return (
    <div
      className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-px-2 ${getStatusClass(
        props.value
      )}`}
    >
      {displayValue}
    </div>
  );
};

const currencyFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const decimalFormatter = (params: { value: any }) => {
  const val = parseFloat(params.value);
  if (params.value == null || isNaN(val)) return "";
  return val.toFixed(2);
};

// Fit columns to the grid width, guarding against the size-changed/ready event
// firing while the grid is hidden in an inactive tab or mid-teardown — in those
// cases the internal API is gone and sizeColumnsToFit() throws.
const safeSizeColumnsToFit = (params: { api?: any }) => {
  const api = params.api;
  if (!api || api.isDestroyed?.()) return;
  try {
    api.sizeColumnsToFit();
  } catch {
    /* grid not ready / no longer mounted — ignore */
  }
};

// ── Per-plant grid (mirrors PlantHoursDG pattern) ──────────────────────────
interface PlantPayrollGridProps {
  plantName: string;
  rows: PayrollReviewRow[];
  columnDefs: ColDef[];
  defaultColDef: ColDef;
  periodStart: string;
  onFinalized: () => void;
  onRowsStatusChange: (
    bbIds: PayrollReviewRow["B&B ID"][],
    status: "Finalized" | "Review"
  ) => void;
}

function PlantPayrollGrid({
  plantName,
  rows,
  columnDefs,
  defaultColDef,
  periodStart,
  onFinalized,
  onRowsStatusChange,
}: PlantPayrollGridProps) {
  const [open, setOpen] = useState(true);
  // Status badge the user clicked to filter the grid. null = show all rows.
  // Counts shown on the badges always reflect the full, unfiltered `rows`.
  const [badgeFilter, setBadgeFilter] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<PayrollReviewRow[]>([]);
  const [detailRow, setDetailRow] = useState<PayrollReviewRow | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  // "$0 Paystub" confirmation flow — mirrors the finalize flow but posts
  // zero_values so the backend persists empty (0 / blank) stub values.
  const [zeroConfirmOpen, setZeroConfirmOpen] = useState(false);
  const [zeroFinalizing, setZeroFinalizing] = useState(false);
  const [zeroListOpen, setZeroListOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [zeroAmountOpen, setZeroAmountOpen] = useState(false);
  const [zeroAmountList, setZeroAmountList] = useState<PayrollReviewRow[]>([]);
  const [zeroAmountOnly, setZeroAmountOnly] = useState(false);
  const canSelect = isSelectablePeriod(periodStart);
  const [voiding, setVoiding] = useState(false);
  const [voidListOpen, setVoidListOpen] = useState(false);
  const [checkBlockOpen, setCheckBlockOpen] = useState(false);
  const [checkBlockRows, setCheckBlockRows] = useState<PayrollReviewRow[]>([]);
  const [missingBankInfo, setMissingBankInfo] = useState<
    { bb_id: string; payee: string | null; missing_fields: string[] }[]
  >([]);
  const [missingBankInfoOpen, setMissingBankInfoOpen] = useState(false);
  const [validating, setValidating] = useState(false);
  const [diffData, setDiffData] = useState<PaystubDifferenceResponse | null>(
    null
  );
  const [diffLoading, setDiffLoading] = useState(false);
  const [generatingDiff, setGeneratingDiff] = useState(false);
  const [diffConfirmOpen, setDiffConfirmOpen] = useState(false);

  // Fetch the calculated-vs-saved diff whenever the user opens the detail
  // modal on a paystub flagged for edit. Skipped for non-edit rows.
  useEffect(() => {
    if (!detailRow || detailRow.Edit !== true || !detailRow["Paystub ID"]) {
      setDiffData(null);
      return;
    }
    let cancelled = false;
    setDiffLoading(true);
    getPaystubDifference(detailRow["Paystub ID"] as number)
      .then((res) => {
        if (!cancelled) setDiffData(res.data);
      })
      .catch(() => {
        if (!cancelled) setDiffData(null);
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [detailRow]);

  const handleGenerateDifferencePaystub = async () => {
    if (!detailRow?.["Paystub ID"]) return;
    setGeneratingDiff(true);
    try {
      await generateDifferencePaystub(detailRow["Paystub ID"] as number);
      setDiffConfirmOpen(false);
      setDetailRow(null);
      onFinalized();
    } catch (err: any) {
      alert(
        err?.response?.data?.error || "Failed to generate difference paystub."
      );
    } finally {
      setGeneratingDiff(false);
    }
  };

  const { handleCellContextMenu, ContextMenuComponent } = AgGridContextMenu({});
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const onSelectionChanged = (event: any) => {
    const selectedNodes = event.api.getSelectedNodes();
    setSelectedRows(selectedNodes.map((node: any) => node.data));
  };

  const handleRowDoubleClicked = (event: any) => {
    setDetailRow(event.data as PayrollReviewRow);
  };

  // "Zero Hours" rows are finalizable alongside "Review" rows. They carry a
  // $0 amount, so the zero-amount confirmation below still warns before
  // generating their paystubs.
  const reviewRows = selectedRows.filter(
    (r) => r.Status === "Review" || r.Status === "Zero Hours"
  );
  const finalizedRows = selectedRows.filter((r) => r.Status === "Finalized");
  const hasMixedSelection = reviewRows.length > 0 && finalizedRows.length > 0;
  const hasEditedFinalizedSelection = finalizedRows.some(
    (r) => r.Edit === true
  );

  const handleFinalizeSelected = async () => {
    if (reviewRows.length === 0) {
      alert(
        "No selected rows with status 'Review' or 'Zero Hours' to finalize."
      );
      return;
    }
    const zeroAmountRows = reviewRows.filter(
      (r) => !r.Amount || parseFloat(String(r.Amount)) === 0
    );
    if (zeroAmountRows.length > 0) {
      setZeroAmountList(zeroAmountRows);
      const allZero = zeroAmountRows.length === reviewRows.length;
      setZeroAmountOnly(allZero);
      setZeroAmountOpen(true);
      return;
    }

    const directDepositRows = reviewRows.filter(
      (r) => r["Pay Type"] === "Direct Deposit"
    );
    if (directDepositRows.length > 0) {
      setValidating(true);
      try {
        const bb_ids = directDepositRows.map((r) => r["B&B ID"]);
        const response = await validateDirectDeposit({ bb_ids });
        const missing = response.data?.missing ?? [];
        if (missing.length > 0) {
          setMissingBankInfo(missing);
          setMissingBankInfoOpen(true);
          return;
        }
      } catch (err: any) {
        alert(
          err?.response?.data?.error || "Failed to validate worker bank info."
        );
        return;
      } finally {
        setValidating(false);
      }
    }

    setConfirmOpen(true);
  };

  const handleCancelFinalized = () => {
    const rowsWithChecks = finalizedRows.filter(
      (r) => r["Check Number"] != null && r["Check Number"] !== ""
    );
    if (rowsWithChecks.length > 0) {
      setCheckBlockRows(rowsWithChecks);
      setCheckBlockOpen(true);
      return;
    }
    setVoidConfirmOpen(true);
  };

  const handleConfirmVoid = async () => {
    setVoiding(true);
    try {
      const bb_ids = finalizedRows.map((r) => r["B&B ID"]);
      await voidSelectedPaystubs({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
      });
      setVoidConfirmOpen(false);
      setSelectedRows([]);
      onRowsStatusChange(bb_ids, "Review");
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to void paystubs.");
    } finally {
      setVoiding(false);
    }
  };

  const handleConfirmFinalize = async () => {
    setFinalizing(true);
    try {
      const bb_ids = reviewRows.map((r) => r["B&B ID"]);
      await generateSelectedPaystubs({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
      });
      setConfirmOpen(false);
      setSelectedRows([]);
      onRowsStatusChange(bb_ids, "Finalized");
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to generate paystubs.");
    } finally {
      setFinalizing(false);
    }
  };

  // "$0 Paystub": same selection as Finalize (Review / Zero Hours rows) but the
  // resulting stubs carry all-zero values. Skips the zero-amount and bank-info
  // checks since no money moves.
  const handleZeroPaystub = () => {
    if (reviewRows.length === 0) {
      alert(
        "No selected rows with status 'Review' or 'Zero Hours' to finalize."
      );
      return;
    }
    setZeroConfirmOpen(true);
  };

  const handleConfirmZeroPaystub = async () => {
    setZeroFinalizing(true);
    try {
      const bb_ids = reviewRows.map((r) => r["B&B ID"]);
      await generateSelectedPaystubs({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
        zero_values: true,
      });
      setZeroConfirmOpen(false);
      setSelectedRows([]);
      onFinalized();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Failed to generate $0 paystubs.");
    } finally {
      setZeroFinalizing(false);
    }
  };

  const reviewCount = rows.filter((r) => r.Status === "Review").length;
  const finalizedCount = rows.filter((r) => r.Status === "Finalized").length;
  const missingCount = rows.filter((r) => r.Status === "Missing").length;
  const zeroHoursCount = rows.filter((r) => r.Status === "Zero Hours").length;

  // Rows shown in the grid: filtered by the clicked badge, or all rows when no
  // badge filter is active. The counts above intentionally stay on full `rows`.
  const displayedRows =
    badgeFilter == null ? rows : rows.filter((r) => r.Status === badgeFilter);

  const toggleBadgeFilter = (status: string | null) =>
    setBadgeFilter((prev) => (prev === status ? null : status));

  return (
    <div className="tw-w-full tw-mb-3">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
        <h2 className="tw-rounded tw-p-1 tw-bg-[#2642c9] tw-text-white">
          {plantName}
        </h2>
        <div className="tw-flex tw-items-center tw-gap-2">
          {(
            [
              {
                status: "Review",
                count: reviewCount,
                base: "tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300",
                active: "tw-ring-2 tw-ring-yellow-500",
              },
              {
                status: "Finalized",
                count: finalizedCount,
                base: "tw-bg-green-100 tw-text-green-800 tw-border-green-300",
                active: "tw-ring-2 tw-ring-green-500",
              },
              {
                status: "Missing",
                count: missingCount,
                base: "tw-bg-red-100 tw-text-red-800 tw-border-red-300",
                active: "tw-ring-2 tw-ring-red-500",
              },
              {
                status: "Zero Hours",
                count: zeroHoursCount,
                base: "tw-bg-blue-100 tw-text-blue-800 tw-border-blue-300",
                active: "tw-ring-2 tw-ring-blue-500",
              },
            ] as const
          ).map((badge) => (
            <button
              key={badge.status}
              type="button"
              onClick={() => toggleBadgeFilter(badge.status)}
              title={`Filter by ${badge.status}`}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-border ${
                badge.base
              } ${badgeFilter === badge.status ? badge.active : ""}`}
            >
              <span className="tw-font-semibold">{badge.count}</span>
              <span className="tw-text-xs">{badge.status}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => toggleBadgeFilter(null)}
            title="Show all rows"
            className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-border tw-bg-gray-100 tw-text-gray-700 tw-border-gray-300 ${
              badgeFilter == null ? "tw-ring-2 tw-ring-gray-500" : ""
            }`}
          >
            <span className="tw-font-semibold">{rows.length}</span>
            <span className="tw-text-xs">Total</span>
          </button>
          <Button
            sx={{ color: "#2642c9", textTransform: "none", minWidth: 0 }}
            onClick={() => setOpen(!open)}
          >
            {open ? "Hide" : "Show"}
          </Button>
        </div>
      </div>
      <Collapse in={open}>
        <div className="tw-w-full">
          <div
            className="ag-theme-balham tw-w-full"
            style={{ height: Math.min(displayedRows.length * 28 + 56, 400) }}
            onContextMenu={handleContextMenu}
          >
            <AgGridReact
              rowData={displayedRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              isRowSelectable={() => canSelect}
              onSelectionChanged={onSelectionChanged}
              onCellContextMenu={handleCellContextMenu}
              onRowDoubleClicked={handleRowDoubleClicked}
              animateRows={true}
              pagination={false}
              onGridReady={safeSizeColumnsToFit}
              onGridSizeChanged={safeSizeColumnsToFit}
            />
            {ContextMenuComponent}
          </div>
          <div className="tw-border-l tw-border-r tw-border-b tw-border-gray-300 tw-p-1 tw-bg-white tw-flex tw-gap-2">
            <Button
              color="primary"
              sx={{ bgcolor: "#2642c9", textTransform: "none" }}
              variant="contained"
              startIcon={<ApprovalIcon />}
              onClick={handleFinalizeSelected}
              disabled={
                reviewRows.length === 0 || hasMixedSelection || validating
              }
            >
              {validating ? "Validating..." : "Finalize Selected"}
            </Button>
            <Button
              sx={{ bgcolor: "#6b7280", textTransform: "none" }}
              variant="contained"
              startIcon={<MoneyOffIcon />}
              onClick={handleZeroPaystub}
              disabled={
                reviewRows.length === 0 || hasMixedSelection || validating
              }
            >
              $0 Paystub
            </Button>
            <Button
              color="error"
              sx={{ textTransform: "none" }}
              variant="contained"
              startIcon={<CancelIcon />}
              onClick={handleCancelFinalized}
              disabled={
                finalizedRows.length === 0 ||
                hasMixedSelection ||
                hasEditedFinalizedSelection
              }
            >
              Cancel Finalized
            </Button>
            {hasMixedSelection && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Cannot mix Review/Zero Hours and Finalized selections
              </span>
            )}
            {!hasMixedSelection && hasEditedFinalizedSelection && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Cannot cancel a paystub that is in edit mode
              </span>
            )}
          </div>
        </div>
      </Collapse>

      {/* Row detail modal */}
      <Dialog
        open={detailRow !== null}
        onClose={() => setDetailRow(null)}
        maxWidth={detailRow?.Edit === true ? "lg" : "md"}
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {detailRow?.Payee ?? "Detail"}
          <IconButton onClick={() => setDetailRow(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailRow &&
            (() => {
              const orderedFields = columnDefs
                .map((c) => c.field)
                .filter(Boolean) as string[];
              const entries = orderedFields
                .filter((field) => field in detailRow)
                .map(
                  (field) => [field, (detailRow as any)[field]] as [string, any]
                );

              const currencyFields = [
                "Amount",
                "Gross Pay",
                "Tax",
                "Pay Rate Value",
                "Overtime Rate",
                "Other",
                "Other Additions",
                "Admin Fee",
                "Rent",
                "Holiday",
                "PTO",
              ];
              const decimalFields = ["Hours"];

              const formatValue = (key: string, value: any) => {
                if (value == null) return "—";
                if (currencyFields.includes(key))
                  return currencyFormatter({ value });
                if (decimalFields.includes(key))
                  return decimalFormatter({ value });
                return String(value);
              };

              const showDiff = detailRow.Edit === true;
              const diffMap = diffData?.diff ?? {};
              const calculatedMap = diffData?.calculated ?? {};

              const renderDiffCell = (key: string) => {
                if (diffLoading) {
                  return (
                    <span className="tw-text-gray-400 tw-text-xs">
                      Loading…
                    </span>
                  );
                }
                if (!diffData) {
                  return <span className="tw-text-gray-400">—</span>;
                }
                const fieldDiff = diffMap[key];
                if (!fieldDiff) {
                  // Field unchanged — show calculated value (same as saved) muted.
                  const calc = calculatedMap[key];
                  return (
                    <span className="tw-text-gray-400">
                      {calc == null ? "—" : formatValue(key, calc)}
                    </span>
                  );
                }
                const delta = fieldDiff.delta;
                const isNumeric = typeof delta === "number";
                const deltaColor =
                  isNumeric && delta > 0
                    ? "tw-text-red-700"
                    : isNumeric && delta < 0
                    ? "tw-text-red-700"
                    : "tw-text-gray-600";
                const deltaText = isNumeric
                  ? `${delta > 0 ? "+" : ""}${currencyFormatter({
                      value: delta,
                    })}`
                  : null;
                return (
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-font-medium">
                      {fieldDiff.calculated == null
                        ? "—"
                        : formatValue(key, fieldDiff.calculated)}
                    </span>
                    {deltaText && (
                      <span className={`tw-text-xs ${deltaColor}`}>
                        {deltaText}
                      </span>
                    )}
                  </div>
                );
              };

              return (
                <Table size="small">
                  {showDiff && (
                    <thead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            width: "25%",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                          }}
                        ></TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            width: "37%",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                          }}
                        >
                          Saved
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            width: "38%",
                            backgroundColor: "#f3f4f6",
                            color: "#374151",
                          }}
                        >
                          Recalculated
                        </TableCell>
                      </TableRow>
                    </thead>
                  )}
                  <TableBody>
                    {entries.map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            width: showDiff ? "25%" : "30%",
                          }}
                        >
                          {key}
                        </TableCell>
                        <TableCell sx={{ width: showDiff ? "37%" : undefined }}>
                          {key === "Status" ? (
                            <span
                              className={
                                value === "Review"
                                  ? "tw-bg-yellow-100 tw-text-yellow-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Finalized"
                                  ? "tw-bg-green-100 tw-text-green-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Missing"
                                  ? "tw-bg-red-100 tw-text-red-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : value === "Zero Hours"
                                  ? "tw-bg-blue-100 tw-text-blue-800 tw-px-2 tw-py-0.5 tw-rounded"
                                  : ""
                              }
                            >
                              {String(value ?? "—")}
                            </span>
                          ) : (
                            formatValue(key, value)
                          )}
                        </TableCell>
                        {showDiff && (
                          <TableCell sx={{ width: "38%" }}>
                            {renderDiffCell(key)}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              );
            })()}
        </DialogContent>
        {detailRow?.Edit === true &&
          (() => {
            const amountDelta = diffData?.diff?.["Amount"]?.delta;
            if (amountDelta == null || amountDelta === 0) return null;
            const isPositive = amountDelta > 0;
            return (
              <DialogActions sx={{ p: 2, gap: 1 }}>
                {!isPositive && (
                  <span className="tw-text-red-600 tw-text-sm tw-mr-auto">
                    Cannot generate a paystub with a lower amount than the saved
                    value.
                  </span>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ textTransform: "none" }}
                  onClick={() => setDiffConfirmOpen(true)}
                  disabled={!isPositive || generatingDiff}
                  startIcon={
                    generatingDiff ? (
                      <Spinner className="tw-w-4 tw-h-4" />
                    ) : (
                      <ApprovalIcon />
                    )
                  }
                >
                  {generatingDiff
                    ? "Generating..."
                    : "Generate Difference Paystub"}
                </Button>
              </DialogActions>
            );
          })()}
      </Dialog>

      {/* Difference Paystub confirmation modal */}
      <Dialog
        open={diffConfirmOpen}
        onClose={() => !generatingDiff && setDiffConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Difference Paystub Generation</DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to generate a difference paystub for{" "}
            <strong>{detailRow?.Payee ?? "this worker"}</strong>
            {detailRow?.["B&B ID"] != null && (
              <> (B&B ID: {String(detailRow["B&B ID"])})</>
            )}
            .
          </p>
          {(() => {
            const delta = diffData?.diff?.["Amount"]?.delta;
            if (delta == null) return null;
            return (
              <p className="tw-text-sm tw-text-gray-700">
                Amount difference:{" "}
                <strong>
                  {delta > 0 ? "+" : ""}
                  {currencyFormatter({ value: delta })}
                </strong>
              </p>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDiffConfirmOpen(false)}
            disabled={generatingDiff}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleGenerateDifferencePaystub}
            disabled={generatingDiff}
            startIcon={
              generatingDiff ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <ApprovalIcon />
              )
            }
          >
            {generatingDiff ? "Generating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finalize confirmation modal */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Paystub Generation</DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to generate paystubs for{" "}
            <strong>{reviewRows.length}</strong> worker(s) with status "Review"
            or "Zero Hours".
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#2642c9", mb: 1 }}
            onClick={() => setListOpen(!listOpen)}
          >
            {listOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={listOpen}>
            <ul className="tw-list-disc tw-pl-5">
              {reviewRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]})
                </li>
              ))}
            </ul>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={finalizing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleConfirmFinalize}
            disabled={finalizing}
            startIcon={
              finalizing ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <ApprovalIcon />
              )
            }
          >
            {finalizing ? "Generating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* $0 Paystub confirmation modal */}
      <Dialog
        open={zeroConfirmOpen}
        onClose={() => setZeroConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#ed6c02" }}>
          Confirm $0 Paystub Generation
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to generate <strong>$0.00</strong> paystubs (all
            values zeroed, blank strings) for{" "}
            <strong>{reviewRows.length}</strong> worker(s) with status "Review"
            or "Zero Hours".
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#2642c9", mb: 1 }}
            onClick={() => setZeroListOpen(!zeroListOpen)}
          >
            {zeroListOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={zeroListOpen}>
            <ul className="tw-list-disc tw-pl-5">
              {reviewRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]})
                </li>
              ))}
            </ul>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setZeroConfirmOpen(false)}
            disabled={zeroFinalizing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#ed6c02", textTransform: "none" }}
            onClick={handleConfirmZeroPaystub}
            disabled={zeroFinalizing}
            startIcon={
              zeroFinalizing ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <MoneyOffIcon />
              )
            }
          >
            {zeroFinalizing ? "Generating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Void confirmation modal */}
      <Dialog
        open={voidConfirmOpen}
        onClose={() => setVoidConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Void Paystubs</DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to void paystubs for{" "}
            <strong>{finalizedRows.length}</strong> worker(s):
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#d32f2f", mb: 1 }}
            onClick={() => setVoidListOpen(!voidListOpen)}
          >
            {voidListOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={voidListOpen}>
            <ul className="tw-list-disc tw-pl-5">
              {finalizedRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]})
                </li>
              ))}
            </ul>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setVoidConfirmOpen(false)}
            disabled={voiding}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={handleConfirmVoid}
            disabled={voiding}
            startIcon={
              voiding ? <Spinner className="tw-w-4 tw-h-4" /> : <CancelIcon />
            }
          >
            {voiding ? "Voiding..." : "Confirm Void"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zero amount warning modal */}
      <Dialog
        open={zeroAmountOpen}
        onClose={() => setZeroAmountOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: zeroAmountOnly ? "#ed6c02" : "#d32f2f" }}>
          {zeroAmountOnly ? "Warning: Zero Amount Paystubs" : "Cannot Finalize"}
        </DialogTitle>
        <DialogContent dividers>
          {zeroAmountOnly ? (
            <p className="tw-mb-2">
              You are about to generate paystubs for{" "}
              <strong>{zeroAmountList.length}</strong> worker(s) with an amount
              of <strong>$0.00</strong>. Are you sure you want to proceed?
            </p>
          ) : (
            <p className="tw-mb-2">
              Cannot finalize. The following{" "}
              <strong>{zeroAmountList.length}</strong> worker(s) have an amount
              of <strong>$0.00</strong> mixed with non-zero selections:
            </p>
          )}
          <ul className="tw-list-disc tw-pl-5">
            {zeroAmountList.map((r) => (
              <li key={String(r["B&B ID"])}>
                {r.Payee} (B&B ID: {r["B&B ID"]})
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setZeroAmountOpen(false)}
            disabled={finalizing}
            sx={{ textTransform: "none" }}
          >
            {zeroAmountOnly ? "Cancel" : "Close"}
          </Button>
          {zeroAmountOnly && (
            <Button
              variant="contained"
              sx={{ bgcolor: "#ed6c02", textTransform: "none" }}
              onClick={() => {
                setZeroAmountOpen(false);
                setConfirmOpen(true);
              }}
              disabled={finalizing}
              startIcon={<ApprovalIcon />}
            >
              Proceed
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Check block error modal */}
      <Dialog
        open={checkBlockOpen}
        onClose={() => setCheckBlockOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#d32f2f" }}>
          Cannot Cancel Finalized
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            The following <strong>{checkBlockRows.length}</strong> worker(s)
            have an issued check that must be voided first:
          </p>
          <ul className="tw-list-disc tw-pl-5">
            {checkBlockRows.map((r) => (
              <li key={String(r["B&B ID"])}>
                {r.Payee} — Check #{r["Check Number"]}
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setCheckBlockOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Missing-bank-info modal — direct deposit paystubs only. Blocks
          finalize until missing bank_account / routing_number is fixed. */}
      <Dialog
        open={missingBankInfoOpen}
        onClose={() => {
          setMissingBankInfoOpen(false);
          setMissingBankInfo([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#d32f2f" }}>
          Cannot Finalize Direct Deposits
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            The following <strong>{missingBankInfo.length}</strong> worker(s)
            are missing the bank info required for direct deposit. Update each
            worker before finalizing.
          </p>
          <ul className="tw-list-disc tw-pl-5">
            {missingBankInfo.map((m) => {
              const labelMap: Record<string, string> = {
                bank_account: "bank account",
                routing_number: "routing number",
                worker_not_found: "worker not found",
              };
              const fields = m.missing_fields
                .map((f) => labelMap[f] ?? f)
                .join(", ");
              return (
                <li key={m.bb_id}>
                  {m.payee ?? "(unknown)"} (B&B ID: {m.bb_id}) — missing:{" "}
                  {fields}
                </li>
              );
            })}
          </ul>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setMissingBankInfoOpen(false);
              setMissingBankInfo([]);
            }}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ── Checks tab types & grid ───────────────────────────────────────────────

interface ChecksReviewRow {
  "Plant ID": number | null;
  "B&B ID": string | number | null;
  Payee: string | null;
  "Payee Street": string | null;
  "Payee City": string | null;
  "Payee State": string | null;
  "Payee Zip": string | null;
  Memo: string | null;
  Amount: number | null;
  "Gross Pay": number | null;
  Tax: number | null;
  "Check Number": string | number | null;
  "Check Status": string | null;
  "Issue Date": string | null;
  "Pay Period": string | null;
  "Paystub ID": number | null;
  "Check ID": number | null;
  Status: string | null;
  PTO: number | null;
  Holiday: number | null;
  "Admin Fee": number | null;
  Rent: number | null;
  "Other Deduction": number | null;
  Hours: number | null;
  "Pay Rate Value": number | string | null;
  "OT Hours": number | null;
  "OT Rate": number | null;
  "Bank Name": string | null;
  "Routing Number": string | null;
  "Account Number": string | null;
  "Print Count": number | null;
  "Cancel Reason": string | null;
  "Payment File": string | null;
  "Payment Cancel File": string | null;
  Edit?: boolean | null;
  "Source Paystub ID"?: number | null;
}

interface PaymentFileRow {
  id: number;
  filename: string;
  account_number: string;
  bank_account_id: number;
  items_count: number;
  total_amount: number;
  created_by: string | null;
  created_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  delivery_date: string | null;
  file_id: string | null;
}

interface PaymentPreviewCheck {
  id: number;
  check_number: string;
  payee: string;
  amount: number;
  issue_date: string | null;
  memo: string | null;
  bank_account_id: number;
  type: "Issue" | "Void";
}

// ── ACH daily-cap split plan (direct deposit only) ──
interface AchSplitPayment {
  id: number;
  payee: string;
  amount: number;
}

interface AchSplitDay {
  day: number;
  processing_date: string;
  delivery_date: string;
  file_id: string;
  items_count: number;
  total_amount: number;
  payments: AchSplitPayment[];
}

interface AchSplitPlan {
  feasible: boolean;
  reason: string | null;
  daily_cap: number;
  eligible_count: number;
  days: AchSplitDay[];
  overflow: AchSplitPayment[];
}

function fmtMoney(val: number | null | undefined): string {
  if (val == null) return "";
  return `$${val.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface CheckLayoutModalProps {
  row: ChecksReviewRow;
  onClose: () => void;
}

// ── Check template mirrored from the Checkeeper reference check ─────────────
// Every coordinate below (in pt, 1/72 in, measured from the top-left of the
// 8.5" x 11" page) was measured from a bank-accepted, Checkeeper-produced
// check PDF for this account, so the printed output is a drop-in replacement
// for the layout the bank has been accepting. Check-on-top stock: the check's
// bottom (perforation) edge is at 3.5" (252pt).
//
// MICR line: the clear band is the bottom 5/8" of the check. With
// bottom: 0.125in and lineHeight 1, headless-Chrome print measurement puts
// our E-13B baseline 0.198" above the perforation (half-leading raises it
// above the CSS bottom offset) — just above the X9.100-160-1 nominal of
// 3/16" and inside the ±1/16" band; the reference check sits at 0.125", the
// band's lower edge, so both comply. The horizontal position anchors the
// transit field at X9 positions 33-43 (see micrRightOffsetIn), keeping the
// amount field (positions 1-12) clear for the bank's encoding.
//
// MICR font size: micrenc.ttf has a 0.5em advance width and ~0.46em digit
// ink height, so 18pt yields the E-13B spec: 18 x 0.5 = 9pt = 0.125"/char
// pitch (8 chars/inch, matching the reference exactly) and ~0.114" character
// height. Do not change the size without redoing that math.
//
// Print at 100% scale with browser headers/footers disabled, and re-verify a
// sample against a MICR position gauge (or the bank's test-deck read) after
// any layout change.
const CHECK_PORTION_HEIGHT = "3.5in";
const MICR_BOTTOM_OFFSET = "0.125in";
const MICR_FONT_SIZE = "18pt";

// CourierNewPSMT in the reference PDF
const CHECK_FONT = "'Courier New', Courier, monospace";

export function CheckPrintContent({ row }: { row: ChecksReviewRow }) {
  const amount = row.Amount ?? 0;
  const micrReady = hasCompleteMicrData(
    row["Routing Number"],
    row["Account Number"],
    row["Check Number"]
  );

  // Absolute placement at measured reference coordinates (pt from page
  // top-left). The reference uses a uniform 13.5pt line advance.
  const at = (
    top: number,
    left: number | null,
    fontSizePt: number,
    extra?: React.CSSProperties
  ): React.CSSProperties => ({
    position: "absolute",
    top: `${top}pt`,
    ...(left != null ? { left: `${left}pt` } : {}),
    fontFamily: CHECK_FONT,
    fontSize: `${fontSizePt}pt`,
    lineHeight: "13.5pt",
    ...extra,
  });

  // Stub line with dot leaders, e.g. "Payrate. . . . . $ 20 /hr"
  const dotLine = (label: string, value: string) => (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        fontFamily: CHECK_FONT,
        fontSize: "9.75pt",
        lineHeight: "13.5pt",
      }}
    >
      <span style={{ whiteSpace: "nowrap" }}>{label}</span>
      <span
        style={{
          flex: 1,
          margin: "0 3pt",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        {". ".repeat(40)}
      </span>
      <span style={{ whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );

  return (
    <div
      className="check-print-content"
      style={{
        position: "relative",
        width: "8.5in",
        height: "11in",
        boxSizing: "border-box",
        overflow: "hidden",
        fontFamily: CHECK_FONT,
      }}
    >
      {/* App.css declares `* { font-family: Arial, ... }`, which applies
          Arial directly to every element and defeats font inheritance —
          a real print run came out with the non-inline-styled children in
          Arial. Restore inheritance inside the check so everything follows
          the root's Courier New; the MICR and Tahoma elements still win via
          their inline styles. */}
      <style>{`.check-print-content * { font-family: inherit; }`}</style>
      {/* ── CHECK PORTION (top 3.5in) ── fixed height so the MICR clear band
          never moves; overflow hidden so long content can't reach it */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: CHECK_PORTION_HEIGHT,
          overflow: "hidden",
        }}
      >
        {/* Check number, top right */}
        <div style={at(12.5, null, 12, { right: "19pt", fontWeight: "bold" })}>
          {row["Check Number"]}
        </div>

        {/* Logo + company block */}
        <img
          src="/logo.png"
          alt="Buildings & Builders"
          style={{
            position: "absolute",
            top: "27.8pt",
            left: "33pt",
            height: "44.2pt",
          }}
        />
        <div style={at(27.5, 138.3, 7.5)}>
          <div>Buildings &amp; Builders Co.</div>
          <div>6740 Huntley Rd, Suite H</div>
          <div>Columbus, OH 43229</div>
        </div>

        {/* Drawee bank block, centered on x=351.25pt like the reference.
            Hardcoded: the row's "Bank Name" is the bank account's internal
            label (e.g. "Payroll"), not the drawee bank, and the address
            lines below are the bank's — all constant for this account. */}
        <div
          style={at(27.5, 280, 7.5, { width: "142.5pt", textAlign: "center" })}
        >
          <div>JPMorgan Chase Bank, NA</div>
          <div>2nd Floor</div>
          <div>Tampa, FL 33610</div>
        </div>

        {/* Date */}
        <div style={at(44, 468.2, 12)}>
          <span style={{ fontWeight: "bold" }}>DATE</span>:
        </div>
        <div style={at(44, 506.7, 12)}>{row["Issue Date"]}</div>

        {/* Courtesy amount */}
        <div style={at(81.5, 469.4, 12, { fontWeight: "bold" })}>$</div>
        <div style={at(81.5, 484.1, 12)}>
          {amount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>

        {/* PAY TO THE / ORDER OF, payee (underlined) and legal amount */}
        <div style={at(87.5, 27.5, 7.5, { fontWeight: "bold" })}>
          PAY TO THE
        </div>
        <div style={at(101, 27.5, 7.5, { fontWeight: "bold" })}>ORDER OF:</div>
        <div
          style={at(84.5, 122.4, 12, {
            width: "304pt",
            whiteSpace: "nowrap",
            overflow: "hidden",
          })}
        >
          {row.Payee}
        </div>
        <div
          style={{
            position: "absolute",
            top: "100.5pt",
            left: "124.2pt",
            width: "302.4pt",
            borderTop: "0.8pt solid #000",
          }}
        />
        <div style={at(102.5, 121.2, 12)}>{amountToWords(amount)}</div>

        {/* Memo + payee address block */}
        <div style={at(135.5, 28.1, 7.5, { fontWeight: "bold" })}>MEMO:</div>
        {row.Memo && <div style={at(131.8, 54, 12)}>{row.Memo}</div>}
        <div style={at(159, 67.3, 9.75)}>
          <div>{row.Payee}</div>
          {row["Payee Street"] && <div>{row["Payee Street"]}</div>}
          {(row["Payee City"] || row["Payee State"] || row["Payee Zip"]) && (
            <div>
              {[row["Payee City"], row["Payee State"]]
                .filter(Boolean)
                .join(", ")}
              {row["Payee Zip"] ? ` ${row["Payee Zip"]}` : ""}
            </div>
          )}
        </div>

        {/* Signature image + line. The image is anchored to the signature
            line (rule at 187pt; check region is 252pt tall) so the ink sits
            on the line regardless of the image's aspect ratio. */}
        <img
          src="/signature.png"
          alt="Authorized Signature"
          style={{
            position: "absolute",
            bottom: `${252 - 187}pt`,
            left: "404pt",
            width: "116.3pt",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "187pt",
            left: "344.6pt",
            width: "238.5pt",
            borderTop: "0.8pt solid #000",
          }}
        />
        <div
          style={at(189, 344.6, 7.5, {
            width: "238.5pt",
            textAlign: "center",
            fontFamily: "Tahoma, sans-serif",
          })}
        >
          AUTHORIZED SIGNATURE
        </div>

        {/* MICR — pinned in the clear band; never render a partial line
            (printing is blocked upstream when MICR data is incomplete, see
            handlePrint; the preview shows a warning instead) */}
        {micrReady ? (
          <div
            style={{
              position: "absolute",
              bottom: MICR_BOTTOM_OFFSET,
              right: `${micrRightOffsetIn(row["Account Number"] ?? "")}in`,
              textAlign: "right",
              whiteSpace: "nowrap",
              fontFamily: "'MICR', monospace",
              fontSize: MICR_FONT_SIZE,
              lineHeight: 1,
            }}
          >
            {formatMicrLine(
              row["Routing Number"] ?? "",
              row["Account Number"] ?? "",
              row["Check Number"] ?? ""
            )}
          </div>
        ) : (
          <div
            style={{
              position: "absolute",
              bottom: MICR_BOTTOM_OFFSET,
              left: "0.25in",
              right: "1.9375in",
              textAlign: "center",
              color: "#dc2626",
              fontWeight: "bold",
              fontSize: 12,
              border: "2px solid #dc2626",
              padding: "2px 8px",
            }}
          >
            CHECK NOT PRINTABLE (INVALID MICR DATA OR AMOUNT) — DO NOT PRESENT
          </div>
        )}
      </div>

      {/* ── STUB PORTION (below the 3.5in perforation) ── */}
      <div style={at(269.5, 37.9, 12, { fontWeight: "bold" })}>
        Buildings &amp; Builders
      </div>
      <div style={at(287, 37.9, 7.5)}>6740 Huntley Rd, Suite H</div>
      <div style={at(300.5, 37.9, 7.5)}>Columbus, OH 43229</div>
      <div style={at(310, 37.9, 12)}>1 (833) 592-8453</div>

      <div style={at(267.5, 331.7, 12)}>
        <div style={{ fontWeight: "bold" }}>Declaración de Pago</div>
        <div>Período: {row["Pay Period"]}</div>
        <div>B&amp;B ID#: {row["B&B ID"]}</div>
        <div>Paystub for: {row.Payee}</div>
      </div>

      {/* Earnings column */}
      <div style={at(350, 39.8, 12, { width: "193pt" })}>
        <div style={{ fontWeight: "bold", marginBottom: "2pt" }}>EARNINGS:</div>
        {dotLine(
          "Payrate",
          row["Pay Rate Value"] != null ? `$ ${row["Pay Rate Value"]} /hr` : "$"
        )}
        {dotLine(
          "Overtime Rate",
          row["OT Rate"] != null ? `$ ${row["OT Rate"]}/hr` : "$"
        )}
        {dotLine("Hours Worked", row.Hours != null ? `${row.Hours} hrs` : "$")}
        {dotLine("Holiday", row.Holiday != null ? fmtMoney(row.Holiday) : "$")}
        {dotLine("Sick Time", "$")}
        {dotLine("Other", "$")}
        {dotLine("Bonus", "$")}
        {dotLine("PTO", row.PTO != null ? fmtMoney(row.PTO) : "$")}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "9.75pt",
            lineHeight: "13.5pt",
            marginTop: "1pt",
          }}
        >
          <span style={{ fontWeight: "bold" }}>Gross Pay Earned:</span>
          <span>{fmtMoney(row["Gross Pay"])}</span>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "9.75pt",
            lineHeight: "13.5pt",
          }}
        >
          <span style={{ fontWeight: "bold" }}>TOTAL NET PAY EARNED:</span>
          <span>{fmtMoney(amount)}</span>
        </div>
      </div>

      {/* Deductions column */}
      <div style={at(350, 331.7, 12, { width: "140pt" })}>
        <div style={{ fontWeight: "bold", marginBottom: "2pt" }}>
          DEDUCTIONS:
        </div>
        {dotLine(
          "Admin Fee",
          row["Admin Fee"] != null ? fmtMoney(row["Admin Fee"]) : "$"
        )}
        {dotLine("Rent Fee", row.Rent != null ? fmtMoney(row.Rent) : "$")}
        {dotLine(
          "Other Fees",
          row["Other Deduction"] != null
            ? fmtMoney(row["Other Deduction"])
            : "$"
        )}
        {dotLine("Fed Tax", row.Tax != null ? fmtMoney(row.Tax) : "$")}
        {dotLine("VISA Bond", "$")}
      </div>

      {/* ── ENVELOPE-WINDOW ADDRESS ── fixed page position measured from the
          reference check; verify against the window envelope stock in use */}
      <div style={at(691.8, 53.8, 12, { lineHeight: "10.5pt" })}>
        <div>{row.Payee}</div>
        {row["Payee Street"] && <div>{row["Payee Street"]}</div>}
        {(row["Payee City"] || row["Payee State"] || row["Payee Zip"]) && (
          <div>
            {[row["Payee City"], row["Payee State"]].filter(Boolean).join(", ")}
            {row["Payee Zip"] ? ` ${row["Payee Zip"]}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

interface CheckLayoutModalProps {
  row: ChecksReviewRow;
  onClose: () => void;
}

function CheckLayoutModal({ row, onClose }: CheckLayoutModalProps) {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          p: "8px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span style={{ fontSize: 13 }}>
          Check #{row["Check Number"]} — {row.Payee}
        </span>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, position: "relative", overflow: "hidden" }}>
        <CheckPrintContent row={row} />
        {row.Status === "Check Cancelled" && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-15deg)",
              fontSize: "80px",
              fontWeight: "bold",
              color: "rgba(220, 38, 38, 0.3)",
              border: "8px solid rgba(220, 38, 38, 0.3)",
              padding: "20px 60px",
              borderRadius: "12px",
              pointerEvents: "none",
              letterSpacing: "8px",
              textTransform: "uppercase",
              zIndex: 10,
            }}
          >
            VOID
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const CheckStatusCellRenderer = (props: {
  value: string;
  data?: ChecksReviewRow;
}) => {
  const isEditingDirectDeposit =
    props.value === "Direct Generated" && props.data?.Edit === true;

  const getStatusClass = (status: string) => {
    if (isEditingDirectDeposit) return "tw-bg-orange-100 tw-text-orange-800";
    if (status === "Pending") return "tw-bg-yellow-100 tw-text-yellow-800";
    if (status === "Check Issued" || status === "Direct Generated")
      return "tw-bg-green-100 tw-text-green-800";
    if (status === "Check Cancelled" || status === "Direct Deposit Canceled")
      return "tw-bg-red-100 tw-text-red-800";
    return "";
  };

  const displayValue = isEditingDirectDeposit ? `Editing` : props.value;

  return (
    <div
      className={`tw-h-full tw-flex tw-items-center tw-justify-center tw-px-2 ${getStatusClass(
        props.value
      )}`}
    >
      {displayValue}
    </div>
  );
};

// ── Check Detail Modal (double-click on Amount column) ──────────────────────
interface CheckDetailModalProps {
  row: ChecksReviewRow;
  onClose: () => void;
}

function CheckDetailModal({ row, onClose }: CheckDetailModalProps) {
  const amount = row.Amount ?? 0;
  const ffMono = "'Courier New', Courier, monospace";

  const field = (label: string, value: React.ReactNode, mono = false) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: "bold", marginBottom: 0 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 13,
          color: mono ? "#2563eb" : "#555",
          fontFamily: mono ? ffMono : undefined,
          lineHeight: 1.3,
        }}
      >
        {value ?? "—"}
      </div>
    </div>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 0, bgcolor: "#f5f5f5", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "12px 16px 8px", position: "relative" }}>
          <h2 style={{ fontSize: 20, fontWeight: "bold", margin: 0 }}>
            {row.Payee}
          </h2>
          <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
            Issued {row["Issue Date"]}
          </div>
          <div style={{ marginTop: 5 }}>
            {row.Status === "Check Cancelled" ||
            row.Status === "Direct Deposit Canceled" ? (
              <div>
                <span
                  style={{
                    fontSize: 10,
                    border: "1px solid #dc2626",
                    borderRadius: 4,
                    padding: "1px 6px",
                    color: "#dc2626",
                    fontWeight: "bold",
                    letterSpacing: 1,
                  }}
                >
                  VOID
                </span>
                {row["Cancel Reason"] && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#666",
                      marginTop: 4,
                      fontStyle: "italic",
                      textAlign: "justify",
                    }}
                  >
                    Reason: {row["Cancel Reason"]}
                  </div>
                )}
              </div>
            ) : (
              <span
                style={{
                  fontSize: 10,
                  border: `1px solid ${
                    (row["Print Count"] ?? 0) > 0 ? "#16a34a" : "#bbb"
                  }`,
                  borderRadius: 4,
                  padding: "1px 6px",
                  color: (row["Print Count"] ?? 0) > 0 ? "#16a34a" : "#999",
                  fontWeight: "bold",
                  letterSpacing: 1,
                }}
              >
                {(row["Print Count"] ?? 0) > 0 ? "PRINTED" : "NOT PRINTED"}
              </span>
            )}
          </div>
          <div
            onClick={onClose}
            style={{
              position: "absolute",
              top: 12,
              right: 14,
              width: 28,
              height: 28,
              borderRadius: "50%",
              backgroundColor: "#222",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <CloseIcon style={{ color: "white", fontSize: 14 }} />
          </div>
        </div>

        {/* Card */}
        <div
          style={{
            margin: "0 12px 12px",
            backgroundColor: "white",
            borderRadius: 8,
            border: "1px solid #e0e0e0",
            padding: "12px 16px",
          }}
        >
          {/* Amount + check number */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontSize: 24, fontWeight: "bold" }}>
                $
                {amount.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div
                style={{
                  fontFamily: ffMono,
                  fontSize: 11,
                  color: "#888",
                  marginTop: 1,
                }}
              >
                {amountToWords(amount)}
              </div>
            </div>
            {row["Check Number"] != null && (
              <div style={{ fontSize: 13, color: "#888", fontWeight: "bold" }}>
                #{row["Check Number"]}
              </div>
            )}
          </div>

          {/* Two-column detail */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "8px 24px",
            }}
          >
            {/* Left */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {field("Pay To:", row.Payee, true)}
              {field("Memo:", row.Memo, true)}
            </div>

            {/* Right */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {field("Date:", row["Issue Date"])}
              {field(
                "Attachment:",
                <span style={{ color: "#aaa", fontStyle: "italic" }}>
                  No document attached.
                </span>
              )}
              {field("Hours:", row.Hours)}
              {field("Gross Pay:", row["Gross Pay"])}
              {field("B&B ID:", row["B&B ID"])}
              {field("Pay Rate Value:", row["Pay Rate Value"])}
              {field("Pay Period:", row["Pay Period"], true)}
              {field("Overtime:", row["OT Rate"])}
              {field("Admin:", row["Admin Fee"])}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface PlantChecksGridProps {
  plantName: string;
  rows: ChecksReviewRow[];
  columnDefs: ColDef[];
  defaultColDef: ColDef;
  periodStart: string;
  onRefresh: () => void;
  paymentType?: "check" | "direct_deposit";
}

function PlantChecksGrid({
  plantName,
  rows,
  columnDefs,
  defaultColDef,
  periodStart,
  onRefresh,
  paymentType = "check",
}: PlantChecksGridProps) {
  const isDirectDeposit = paymentType === "direct_deposit";
  const ISSUED_STATUS = isDirectDeposit ? "Direct Generated" : "Check Issued";
  const CANCELLED_STATUS = isDirectDeposit
    ? "Direct Deposit Canceled"
    : "Check Cancelled";
  const itemNounPlural = isDirectDeposit ? "direct deposits" : "checks";
  // Status badge the user clicked to filter the grid. null = show all rows.
  // Counts shown on the badges always reflect the full, unfiltered `rows`.
  const [badgeFilter, setBadgeFilter] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<ChecksReviewRow[]>([]);
  const [detailRow, setDetailRow] = useState<ChecksReviewRow | null>(null);
  const [detailAmountRow, setDetailAmountRow] =
    useState<ChecksReviewRow | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [cancelReasonModalOpen, setCancelReasonModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelListOpen, setCancelListOpen] = useState(false);
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [printRows, setPrintRows] = useState<ChecksReviewRow[]>([]);
  const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
  const [printConfirmRows, setPrintConfirmRows] = useState<ChecksReviewRow[]>(
    []
  );
  const canSelect = isSelectablePeriod(periodStart);
  const [historyRow, setHistoryRow] = useState<ChecksReviewRow | null>(null);
  const [historyData, setHistoryData] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (printRows.length === 0) return;

    const styleEl = document.createElement("style");
    styleEl.id = "checks-print-style";
    styleEl.textContent = `
      /* Zero page margins so each check page is exactly 8.5in x 11in and
         MICR / envelope-window positions (set in physical inches in
         CheckPrintContent) land where the check stock expects them. The
         print dialog must be at 100% scale with headers/footers off. */
      @page { size: letter; margin: 0; }
      #checks-print-root { position: absolute; top: -9999px; left: -9999px; }
      @media print {
        html, body { margin: 0; padding: 0; }
        body * { visibility: hidden; }
        #checks-print-root, #checks-print-root * { visibility: visible; }
        #checks-print-root { position: absolute; top: 0; left: 0; width: 100%; padding: 0; box-sizing: border-box; }
      }
    `;
    document.head.appendChild(styleEl);

    const checkIds = printRows
      .map((r) => r["Check ID"])
      .filter((id) => id != null);

    const cleanup = () => {
      document.getElementById("checks-print-style")?.remove();
      setPrintRows([]);
    };

    const afterPrint = async () => {
      window.removeEventListener("afterprint", afterPrint);
      cleanup();
      if (checkIds.length > 0) {
        try {
          await printChecks({ check_ids: checkIds as number[] });
        } catch (e) {
          console.error("Failed to record print:", e);
        }
        onRefresh();
      }
    };

    window.addEventListener("afterprint", afterPrint);

    // Wait for all images inside the print root to load before printing
    const printRoot = document.getElementById("checks-print-root");
    const images = printRoot
      ? Array.from(printRoot.querySelectorAll("img"))
      : [];
    const imagePromises = images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) return resolve();
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    );

    let cancelled = false;
    Promise.all(imagePromises).then(() => {
      if (!cancelled) window.print();
    });

    return () => {
      cancelled = true;
      window.removeEventListener("afterprint", afterPrint);
      cleanup();
    };
  }, [printRows, onRefresh]);

  useEffect(() => {
    if (!historyRow?.["Check ID"]) {
      setHistoryData([]);
      return;
    }
    setHistoryLoading(true);
    axiosClient
      .get(`/payroll/check-history/${historyRow["Check ID"]}`)
      .then((res) => {
        const data = res.data;
        setHistoryData(Array.isArray(data) ? data : data?.history || []);
      })
      .catch(() => setHistoryData([]))
      .finally(() => setHistoryLoading(false));
  }, [historyRow]);

  const {
    handleCellContextMenu,
    handleCopyFromContextMenu,
    ContextMenuComponent,
  } = AgGridContextMenu({
    menuItems: [
      {
        label: "Copy Cell Value",
        icon: FiCopy,
        onClick: () => handleCopyFromContextMenu(),
        disabled: (value: string) => !value.length,
      },
      {
        label: "View History",
        icon: FiClock,
        onClick: (_value: string, rowData: ChecksReviewRow) => {
          if (rowData?.["Check ID"]) {
            setHistoryRow(rowData);
          }
        },
        disabled: (_value: string, rowData: ChecksReviewRow) =>
          !rowData?.["Check ID"],
      },
    ],
    // enabledColumns: ["contractor_name", "user_email", "user_id", "description"], // Example of enabling context menu only on specific columns
  });
  const handleContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const onSelectionChanged = (event: any) => {
    const selectedNodes = event.api.getSelectedNodes();
    setSelectedRows(selectedNodes.map((node: any) => node.data));
  };

  const handleCellDoubleClicked = (event: any) => {
    const row = event.data as ChecksReviewRow;
    const colId = event.column?.getColId?.();
    if (!isDirectDeposit && colId === "Check Number") {
      setDetailRow(row);
    } else {
      setDetailAmountRow(row);
    }
  };

  const pendingRows = selectedRows.filter((r) => r.Status === "Pending");
  const issuedRows = selectedRows.filter((r) => r.Status === ISSUED_STATUS);
  const cancelledRows = selectedRows.filter(
    (r) => r.Status === CANCELLED_STATUS
  );
  const hasMixedSelection = pendingRows.length > 0 && issuedRows.length > 0;
  const sentInFileRows = isDirectDeposit
    ? issuedRows.filter((r) => r["Payment File"] != null)
    : [];
  const hasSentInFileSelection = sentInFileRows.length > 0;
  // Edit-flag state — single-row operation (per product decision).
  const singleSelectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
  const singleSelectedIsEdited = singleSelectedRow?.Edit === true;
  const hasEditedSelection = selectedRows.some((r) => r.Edit === true);
  // A paystub generated as a correction ("difference") of another stub is
  // locked from edit/cancel — the original drives those operations.
  const isDifferencePaystub = (r: ChecksReviewRow) =>
    typeof r["Source Paystub ID"] === "number" &&
    (r["Source Paystub ID"] as number) > 0;
  const singleSelectedIsDifference = singleSelectedRow
    ? isDifferencePaystub(singleSelectedRow)
    : false;
  const hasDifferenceSelection = selectedRows.some(isDifferencePaystub);
  const singleSelectedHasPaymentFile = singleSelectedRow
    ? Boolean(singleSelectedRow["Payment File"])
    : false;

  const handlePrint = () => {
    const toPrint = issuedRows.filter((r) => r["Check ID"] != null);
    if (toPrint.length === 0) return;
    // Never print a check with an incomplete MICR line (missing/invalid
    // routing, account, or check number) or a non-positive amount — a
    // partial MICR line or a $0/negative check is guaranteed manual
    // handling or rejection at the bank.
    const unprintable = toPrint.filter(
      (r) =>
        !hasCompleteMicrData(
          r["Routing Number"],
          r["Account Number"],
          r["Check Number"]
        ) || !(typeof r.Amount === "number" && r.Amount > 0)
    );
    if (unprintable.length > 0) {
      const detail = unprintable
        .map((r) => `#${r["Check Number"] ?? "?"} — ${r.Payee ?? "?"}`)
        .join("\n");
      alert(
        `Cannot print: ${unprintable.length} check(s) have missing/invalid ` +
          `MICR data (routing, account, or check number) or a non-positive ` +
          `amount:\n\n${detail}`
      );
      return;
    }
    setPrintConfirmRows(toPrint);
    setPrintConfirmOpen(true);
  };

  const handleConfirmPrint = () => {
    setPrintConfirmOpen(false);
    setPrintRows(printConfirmRows);
    setPrintConfirmRows([]);
  };

  const handleGenerateChecks = async () => {
    if (pendingRows.length === 0) {
      alert(
        `No selected rows with status 'Pending' to generate ${itemNounPlural}.`
      );
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmGenerate = async () => {
    setGenerating(true);
    try {
      const bb_ids = pendingRows.map((r) => r["B&B ID"]);
      await generateChecks({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
        payment_type: paymentType,
      });
      setConfirmOpen(false);
      setSelectedRows([]);
      onRefresh();
    } catch (err: any) {
      alert(
        err?.response?.data?.error || `Failed to generate ${itemNounPlural}.`
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleCancelCheck = () => {
    if (issuedRows.length === 0) {
      alert(`No selected rows with status '${ISSUED_STATUS}' to cancel.`);
      return;
    }
    setCancelReasonModalOpen(true);
  };

  const handleEditPaystubClick = () => {
    if (!singleSelectedRow) return;
    setEditConfirmOpen(true);
  };

  const handleConfirmEditPaystub = async () => {
    if (!singleSelectedRow) return;
    const paystubId = singleSelectedRow["Paystub ID"];
    if (paystubId == null) {
      alert("Selected row has no Paystub ID.");
      return;
    }
    setEditing(true);
    try {
      await setPaystubEdit(paystubId, !singleSelectedIsEdited);
      setEditConfirmOpen(false);
      setSelectedRows([]);
      onRefresh();
    } catch (err: any) {
      alert(
        err?.response?.data?.error || "Failed to update paystub edit flag."
      );
    } finally {
      setEditing(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      alert("Cancel reason is required.");
      return;
    }
    setCancelling(true);
    try {
      const bb_ids = issuedRows.map((r) => r["B&B ID"]);
      await cancelChecks({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        bb_ids,
        cancel_reason: cancelReason,
        payment_type: paymentType,
      });
      setCancelReasonModalOpen(false);
      setCancelReason("");
      setSelectedRows([]);
      onRefresh();
    } catch (err: any) {
      alert(
        err?.response?.data?.error || `Failed to cancel ${itemNounPlural}.`
      );
    } finally {
      setCancelling(false);
    }
  };

  const pendingCount = rows.filter((r) => r.Status === "Pending").length;
  const issuedCount = rows.filter((r) => r.Status === ISSUED_STATUS).length;
  const cancelledCount = rows.filter(
    (r) => r.Status === CANCELLED_STATUS
  ).length;

  // Rows shown in the grid: filtered by the clicked badge, or all rows when no
  // badge filter is active. The counts above intentionally stay on full `rows`.
  const displayedRows =
    badgeFilter == null ? rows : rows.filter((r) => r.Status === badgeFilter);

  const toggleBadgeFilter = (status: string | null) =>
    setBadgeFilter((prev) => (prev === status ? null : status));

  return (
    <div className="tw-w-full tw-mb-3">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
        <h2 className="tw-rounded tw-p-1 tw-bg-[#2642c9] tw-text-white">
          {plantName}
        </h2>
        <div className="tw-flex tw-items-center tw-gap-2">
          {(
            [
              {
                status: "Pending",
                count: pendingCount,
                base: "tw-bg-yellow-100 tw-text-yellow-800 tw-border-yellow-300",
                active: "tw-ring-2 tw-ring-yellow-500",
              },
              {
                status: ISSUED_STATUS,
                count: issuedCount,
                base: "tw-bg-green-100 tw-text-green-800 tw-border-green-300",
                active: "tw-ring-2 tw-ring-green-500",
              },
              {
                status: CANCELLED_STATUS,
                count: cancelledCount,
                base: "tw-bg-red-100 tw-text-red-800 tw-border-red-300",
                active: "tw-ring-2 tw-ring-red-500",
              },
            ] as const
          ).map((badge) => (
            <button
              key={badge.status}
              type="button"
              onClick={() => toggleBadgeFilter(badge.status)}
              title={`Filter by ${badge.status}`}
              className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-border ${
                badge.base
              } ${badgeFilter === badge.status ? badge.active : ""}`}
            >
              <span className="tw-font-semibold">{badge.count}</span>
              <span className="tw-text-xs">{badge.status}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => toggleBadgeFilter(null)}
            title="Show all rows"
            className={`tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded tw-border tw-bg-gray-100 tw-text-gray-700 tw-border-gray-300 ${
              badgeFilter == null ? "tw-ring-2 tw-ring-gray-500" : ""
            }`}
          >
            <span className="tw-font-semibold">{rows.length}</span>
            <span className="tw-text-xs">Total</span>
          </button>
        </div>
      </div>
      <div className="tw-w-full">
        <div
          className="ag-theme-balham tw-w-full"
          style={{
            // Fill the available screen height, but never shrink below the
            // previous fixed 440px floor on short screens.
            height: "calc(100vh - 365px)",
            minHeight: 440,
          }}
          onContextMenu={handleContextMenu}
        >
          <AgGridReact
            rowData={displayedRows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            isRowSelectable={() => canSelect}
            onSelectionChanged={onSelectionChanged}
            onCellContextMenu={handleCellContextMenu}
            onCellDoubleClicked={handleCellDoubleClicked}
            animateRows={true}
            pagination={false}
            onGridReady={safeSizeColumnsToFit}
            onGridSizeChanged={safeSizeColumnsToFit}
            getRowStyle={(params: any) => {
              if (params.data?.Status === CANCELLED_STATUS)
                return { color: "#000000" };
              if (params.data?.Status === ISSUED_STATUS)
                return { color: "#000000" };
              if (params.data?.Status === "Pending")
                return { color: "#2563eb" };
              return undefined;
            }}
          />
          {ContextMenuComponent}
        </div>
        <div className="tw-border-l tw-border-r tw-border-b tw-border-gray-300 tw-p-1 tw-bg-white tw-flex tw-gap-2 ">
          <Button
            color="primary"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            variant="contained"
            startIcon={<ApprovalIcon />}
            onClick={handleGenerateChecks}
            disabled={pendingRows.length === 0 || hasMixedSelection}
          >
            {isDirectDeposit ? "Generate Direct Deposits" : "Generate Checks"}
          </Button>
          <Button
            color="error"
            sx={{ textTransform: "none" }}
            variant="contained"
            startIcon={<CancelIcon />}
            onClick={handleCancelCheck}
            disabled={
              issuedRows.length === 0 ||
              hasMixedSelection ||
              hasSentInFileSelection ||
              hasEditedSelection ||
              hasDifferenceSelection
            }
          >
            {isDirectDeposit ? "Cancel Direct Deposit" : "Cancel Check"}
          </Button>
          {isDirectDeposit && (
            <Button
              sx={{ textTransform: "none" }}
              variant="outlined"
              color={singleSelectedIsEdited ? "warning" : "primary"}
              startIcon={
                singleSelectedIsEdited ? <EditOffIcon /> : <EditIcon />
              }
              onClick={handleEditPaystubClick}
              disabled={
                !singleSelectedRow ||
                singleSelectedIsDifference ||
                !singleSelectedHasPaymentFile
              }
            >
              {singleSelectedIsEdited ? "Cancel Edit" : "Edit Paystub"}
            </Button>
          )}
          {!isDirectDeposit && (
            <Button
              sx={{ textTransform: "none" }}
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={
                issuedRows.length === 0 ||
                hasMixedSelection ||
                cancelledRows.length > 0
              }
            >
              Print
            </Button>
          )}
          {hasMixedSelection && (
            <span className="tw-text-red-500 tw-text-sm tw-self-center">
              Cannot mix Pending and {ISSUED_STATUS} selections
            </span>
          )}
          {!hasMixedSelection &&
            !isDirectDeposit &&
            cancelledRows.length > 0 && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Cannot print cancelled checks
              </span>
            )}
          {/* Hide these "cannot cancel" hints whenever the Edit Paystub
              button is enabled — the user already has an actionable path. */}
          {isDirectDeposit && hasSentInFileSelection && !singleSelectedRow && (
            <span className="tw-text-red-500 tw-text-sm tw-self-center">
              Cannot cancel direct deposits already sent in a file
            </span>
          )}
          {isDirectDeposit &&
            !hasSentInFileSelection &&
            hasEditedSelection &&
            !singleSelectedRow && (
              <span className="tw-text-red-500 tw-text-sm tw-self-center">
                Cannot cancel a paystub that is in edit mode
              </span>
            )}
        </div>
      </div>

      {detailAmountRow && (
        <CheckDetailModal
          row={detailAmountRow}
          onClose={() => setDetailAmountRow(null)}
        />
      )}

      {detailRow && detailRow.Status !== "Pending" && (
        <CheckLayoutModal row={detailRow} onClose={() => setDetailRow(null)} />
      )}

      {/* Pending row detail modal */}
      {detailRow &&
        detailRow.Status === "Pending" &&
        (() => {
          const orderedFields = columnDefs
            .map((c) => c.field)
            .filter(Boolean) as string[];
          const entries = orderedFields
            .filter((field) => field in detailRow)
            .map(
              (field) => [field, (detailRow as any)[field]] as [string, any]
            );
          const mid = Math.ceil(entries.length / 2);
          const leftCol = entries.slice(0, mid);
          const rightCol = entries.slice(mid);
          const currencyFields = [
            "Amount",
            "Gross Pay",
            "Tax",
            "Pay Rate Value",
            "OT Rate",
            "Admin Fee",
            "Rent",
            "Holiday",
            "PTO",
          ];
          const decimalFields = ["Hours", "OT Hours"];
          const formatValue = (key: string, value: any) => {
            if (value == null) return "—";
            if (currencyFields.includes(key))
              return currencyFormatter({ value });
            if (decimalFields.includes(key)) return decimalFormatter({ value });
            return String(value);
          };
          return (
            <Dialog
              open
              onClose={() => setDetailRow(null)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {detailRow.Payee ?? "Detail"}
                <IconButton onClick={() => setDetailRow(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent dividers>
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  {[leftCol, rightCol].map((col, colIdx) => (
                    <Table size="small" key={colIdx}>
                      <TableBody>
                        {col.map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell sx={{ fontWeight: 600, width: "45%" }}>
                              {key}
                            </TableCell>
                            <TableCell>
                              {key === "Status" ? (
                                <span className="tw-bg-yellow-100 tw-text-yellow-800 tw-px-2 tw-py-0.5 tw-rounded">
                                  {String(value ?? "—")}
                                </span>
                              ) : (
                                formatValue(key, value)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ))}
                </div>
              </DialogContent>
            </Dialog>
          );
        })()}

      {/* Generate checks confirmation modal */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isDirectDeposit
            ? "Confirm Direct Deposit Generation"
            : "Confirm Check Generation"}
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            You are about to generate {itemNounPlural} for{" "}
            <strong>{pendingRows.length}</strong> worker(s) with status
            "Pending".
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#2642c9", mb: 1 }}
            onClick={() => setListOpen(!listOpen)}
          >
            {listOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={listOpen}>
            <ul className="tw-list-disc tw-pl-5">
              {pendingRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]})
                </li>
              ))}
            </ul>
          </Collapse>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={generating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleConfirmGenerate}
            disabled={generating}
            startIcon={
              generating ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <ApprovalIcon />
              )
            }
          >
            {generating ? "Generating..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel check reason modal */}
      <Dialog
        open={cancelReasonModalOpen}
        onClose={() => {
          if (!cancelling) {
            setCancelReasonModalOpen(false);
            setCancelReason("");
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isDirectDeposit
            ? "Cancel Direct Deposits - Reason Required"
            : "Cancel Checks - Reason Required"}
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-4">
            You are about to cancel {itemNounPlural} for{" "}
            <strong>{issuedRows.length}</strong> worker(s).
          </p>
          <Button
            size="small"
            sx={{ textTransform: "none", color: "#d32f2f", mb: 2 }}
            onClick={() => setCancelListOpen(!cancelListOpen)}
          >
            {cancelListOpen ? "Hide" : "Show"} contractor list
          </Button>
          <Collapse in={cancelListOpen}>
            <ul className="tw-list-disc tw-pl-5 tw-mb-4">
              {issuedRows.map((r) => (
                <li key={String(r["B&B ID"])}>
                  {r.Payee} (B&B ID: {r["B&B ID"]})
                </li>
              ))}
            </ul>
          </Collapse>
          <div className="tw-mt-4">
            <label className="tw-block tw-mb-2 tw-font-semibold tw-text-gray-700">
              Reason for Cancellation <span className="tw-text-red-600">*</span>
            </label>
            <textarea
              className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-p-2 tw-min-h-[100px] focus:tw-border-blue-500 focus:tw-outline-none"
              placeholder={`Please provide a reason for cancelling these ${itemNounPlural}...`}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              disabled={cancelling}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setCancelReasonModalOpen(false);
              setCancelReason("");
            }}
            disabled={cancelling}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
            onClick={handleConfirmCancel}
            disabled={cancelling || !cancelReason.trim()}
            startIcon={
              cancelling ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <CancelIcon />
              )
            }
          >
            {cancelling ? "Cancelling..." : "Confirm Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit-paystub confirmation — toggles the paystub.edit flag */}
      <Dialog
        open={editConfirmOpen}
        onClose={() => {
          if (!editing) setEditConfirmOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {singleSelectedIsEdited
            ? "Cancel Edit Mode"
            : "Mark Paystub for Edit"}
        </DialogTitle>
        <DialogContent dividers>
          <p className="tw-mb-2">
            {singleSelectedIsEdited
              ? "This will clear the edit flag on the paystub and re-allow cancellation."
              : "This will mark the paystub for edit. Direct deposit cancellation will be blocked while in edit mode."}
          </p>
          {singleSelectedRow && (
            <p className="tw-text-sm tw-text-gray-600">
              <strong>Payee:</strong> {singleSelectedRow.Payee} (B&B ID:{" "}
              {singleSelectedRow["B&B ID"]})
            </p>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setEditConfirmOpen(false)}
            disabled={editing}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color={singleSelectedIsEdited ? "warning" : "primary"}
            sx={{ textTransform: "none" }}
            onClick={handleConfirmEditPaystub}
            disabled={editing}
            startIcon={
              editing ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : singleSelectedIsEdited ? (
                <EditOffIcon />
              ) : (
                <EditIcon />
              )
            }
          >
            {editing
              ? "Saving..."
              : singleSelectedIsEdited
              ? "Confirm Cancel Edit"
              : "Confirm Edit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print confirmation — warns that print count will increase */}
      <Dialog
        open={printConfirmOpen}
        onClose={() => {
          setPrintConfirmOpen(false);
          setPrintConfirmRows([]);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Print</DialogTitle>
        <DialogContent dividers>
          <p>
            You are about to print {printConfirmRows.length} check(s). The print
            count will increase for each check. Continue?
          </p>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setPrintConfirmOpen(false);
              setPrintConfirmRows([]);
            }}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: "none" }}
            onClick={handleConfirmPrint}
            startIcon={<PrintIcon />}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Check History Drawer */}
      {historyRow && (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex">
          <div
            className="tw-flex-1 tw-bg-black/40"
            onClick={() => setHistoryRow(null)}
          />
          <div className="tw-w-full tw-max-w-2xl tw-bg-white tw-h-full tw-shadow-xl tw-border-l tw-border-gray-200 tw-overflow-auto">
            <div className="tw-sticky tw-top-0 tw-bg-white tw-border-b tw-border-gray-200 tw-p-4 tw-z-10">
              <div className="tw-flex tw-items-center tw-justify-between">
                <h3 className="tw-text-lg tw-font-semibold tw-flex tw-items-center tw-gap-2">
                  <FileText size={20} />
                  {isDirectDeposit ? "Direct Deposit History" : "Check History"}
                </h3>
                <button
                  className="tw-p-1 tw-text-gray-500 hover:tw-text-black"
                  onClick={() => setHistoryRow(null)}
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="tw-p-4">
              {/* Check Details */}
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-mb-4">
                <h4 className="tw-text-sm tw-font-semibold tw-mb-2 tw-text-gray-700">
                  {isDirectDeposit ? "Direct Deposit Details" : "Check Details"}
                </h4>
                <div className="tw-space-y-1 tw-text-sm">
                  {!isDirectDeposit && (
                    <div>
                      <span className="tw-text-gray-500">Check Number:</span>{" "}
                      <span className="tw-font-mono">
                        #{historyRow["Check Number"]}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="tw-text-gray-500">Payee:</span>{" "}
                    <span>{historyRow.Payee}</span>
                  </div>
                  <div>
                    <span className="tw-text-gray-500">Amount:</span>{" "}
                    <span>
                      {currencyFormatter({ value: historyRow.Amount })}
                    </span>
                  </div>
                  <div>
                    <span className="tw-text-gray-500">Status:</span>{" "}
                    <span
                      className={`tw-font-medium ${
                        historyRow.Status === ISSUED_STATUS
                          ? "tw-text-green-600"
                          : historyRow.Status === CANCELLED_STATUS
                          ? "tw-text-red-600"
                          : "tw-text-yellow-600"
                      }`}
                    >
                      {historyRow.Status}
                    </span>
                  </div>
                  {historyRow["Issue Date"] && (
                    <div>
                      <span className="tw-text-gray-500">Issue Date:</span>{" "}
                      <span>{historyRow["Issue Date"]}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Version History */}
              <div>
                <h4 className="tw-text-sm tw-font-semibold tw-mb-3 tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                  <Clock size={16} />
                  Version History
                </h4>

                {historyLoading && (
                  <div className="tw-text-center tw-py-8 tw-text-gray-500">
                    <Spinner className="tw-w-6 tw-h-6 tw-mx-auto" />
                    <div className="tw-mt-2">Loading history...</div>
                  </div>
                )}

                {!historyLoading && historyData.length === 0 && (
                  <div className="tw-text-center tw-py-8 tw-text-gray-500">
                    No history records found.
                  </div>
                )}

                {!historyLoading && historyData.length > 0 && (
                  <div className="tw-space-y-3">
                    {historyData.map((entry, index) => {
                      const getOperationColor = (operation: string) => {
                        const op = operation?.toUpperCase();
                        if (op === "INSERT")
                          return "tw-bg-blue-100 tw-text-blue-800";
                        if (op === "UPDATE")
                          return "tw-bg-yellow-100 tw-text-yellow-800";
                        if (op === "DELETE")
                          return "tw-bg-red-100 tw-text-red-800";
                        return "tw-bg-gray-100 tw-text-gray-800";
                      };

                      return (
                        <div
                          key={entry.transaction_id ?? index}
                          className="tw-relative tw-pl-6 tw-pb-3 tw-border-l-2 tw-border-gray-200 last:tw-border-l-0 last:tw-pb-0"
                        >
                          <div className="tw-absolute tw-left-0 tw-top-0 tw-w-3 tw-h-3 tw-bg-blue-500 tw-rounded-full -tw-translate-x-[7px]" />

                          <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-shadow-sm">
                            <div className="tw-flex tw-items-start tw-justify-between tw-gap-2 tw-mb-2">
                              <span
                                className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${getOperationColor(
                                  entry.operation
                                )}`}
                              >
                                {entry.operation}
                              </span>
                              <span className="tw-text-xs tw-text-gray-500">
                                {entry.modified_at
                                  ? new Date(entry.modified_at).toLocaleString()
                                  : "N/A"}
                              </span>
                            </div>

                            <div className="tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1 tw-text-sm">
                              {entry.status && (
                                <div>
                                  <span className="tw-text-gray-500">
                                    Status:
                                  </span>{" "}
                                  <span
                                    className={`tw-font-medium ${
                                      entry.status?.toString().toLowerCase() ===
                                      "voided"
                                        ? "tw-text-red-600"
                                        : ""
                                    }`}
                                  >
                                    {entry.status}
                                  </span>
                                </div>
                              )}
                              {entry.print_count != null && (
                                <div>
                                  <span className="tw-text-gray-500">
                                    Print Count:
                                  </span>{" "}
                                  <span>{entry.print_count}</span>
                                </div>
                              )}
                              {entry.cancel_reason && (
                                <div className="tw-text-xs tw-text-gray-500">
                                  Cancel Reason: {entry.cancel_reason}
                                </div>
                              )}
                              {entry.payment_file_name && (
                                <div className="tw-text-xs tw-text-gray-500">
                                  Payment File: {entry.payment_file_name}
                                </div>
                              )}
                              {entry.payment_file_cancel_name && (
                                <div className="tw-text-xs tw-text-gray-500">
                                  Payment Cancel File:{" "}
                                  {entry.payment_file_cancel_name}
                                </div>
                              )}
                            </div>

                            {entry.modified_by && (
                              <div className="tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-100 tw-text-xs tw-text-gray-500">
                                By: {entry.modified_by}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden print content — made visible only during window.print() via injected @media print CSS */}
      <div id="checks-print-root">
        {printRows.map((r, i) => (
          <div
            key={r["Check ID"]}
            style={{ breakAfter: i < printRows.length - 1 ? "page" : "auto" }}
          >
            <CheckPrintContent row={r} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Payment Files tab (used by both Positive Checks and ACH Files) ────────
interface PaymentFilesTabProps {
  paymentType: "check" | "direct_deposit";
  periodStart: string;
  onAfterGenerate?: () => void;
  isPayrollAdmin: boolean;
}

function PaymentFilesTab({
  paymentType,
  periodStart,
  onAfterGenerate,
  isPayrollAdmin,
}: PaymentFilesTabProps) {
  const isDirectDeposit = paymentType === "direct_deposit";
  const fileLabel = isDirectDeposit ? "ACH" : "Payment";
  const itemNoun = isDirectDeposit ? "direct deposit" : "check";

  const [rows, setRows] = useState<PaymentFileRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [previewChecks, setPreviewChecks] = useState<PaymentPreviewCheck[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  // NACHA file-id base, only used when paymentType === "direct_deposit".
  const [fileIdInput, setFileIdInput] = useState<string>("A");

  // ACH daily-cap splitting (direct deposit only). The first processing date
  // must be Mon–Thu (Thursday is the last day that settles by Friday payday);
  // default to the next such weekday. Cap defaults to the $90k soft buffer.
  const nextProcessingIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    // Mon–Thu only (getDay: 0=Sun..6=Sat). Fri(5)/Sat(6)/Sun(0) → roll to Mon.
    while (d.getDay() === 5 || d.getDay() === 6 || d.getDay() === 0) {
      d.setDate(d.getDate() + 1);
    }
    return formatDate(d);
  }, []);
  const [processingDate, setProcessingDate] =
    useState<string>(nextProcessingIso);
  const [dailyCap, setDailyCap] = useState<string>("90000");
  const [achPlan, setAchPlan] = useState<AchSplitPlan | null>(null);
  const [achPlanLoading, setAchPlanLoading] = useState(false);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchPaymentFiles({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        payment_type: paymentType,
      });
      setRows(response.data);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          `Failed to fetch ${fileLabel.toLowerCase()} files`
      );
    } finally {
      setLoading(false);
    }
  }, [periodStart, paymentType, fileLabel]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Recompute the ACH daily-cap split plan for the current inputs. Pure
  // preview — writes nothing. Used on open and whenever the cap / processing
  // date / file id change inside the dialog.
  const loadAchPlan = useCallback(
    async (procDate: string, cap: string, fileId: string) => {
      setAchPlanLoading(true);
      try {
        const response = await previewAchSplit({
          start_date: periodStart,
          end_date: getEndDateStr(periodStart),
          daily_cap: cap,
          processing_date: procDate,
          file_id: fileId,
        });
        setError(null);
        setAchPlan(response.data as AchSplitPlan);
      } catch (err: any) {
        setAchPlan(null);
        setError(
          err?.response?.data?.error || "Failed to compute ACH split plan"
        );
      } finally {
        setAchPlanLoading(false);
      }
    },
    [periodStart]
  );

  const handleGenerateClick = useCallback(async () => {
    setPreviewLoading(true);
    setError(null);
    try {
      const response = await previewPaymentChecks({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        payment_type: paymentType,
      });
      if (response.data.length === 0) {
        setError(
          `No eligible ${itemNoun}s found for ${fileLabel.toLowerCase()} file generation`
        );
        return;
      }
      setPreviewChecks(response.data);
      if (isDirectDeposit) {
        // Reset inputs to defaults and compute the initial split plan.
        setProcessingDate(nextProcessingIso);
        setDailyCap("90000");
        setFileIdInput("A");
        setAchPlan(null);
        await loadAchPlan(nextProcessingIso, "90000", "A");
      }
      setPreviewOpen(true);
    } catch (err: any) {
      setError(
        err?.response?.data?.error || `Failed to load eligible ${itemNoun}s`
      );
    } finally {
      setPreviewLoading(false);
    }
  }, [
    periodStart,
    paymentType,
    itemNoun,
    fileLabel,
    isDirectDeposit,
    nextProcessingIso,
    loadAchPlan,
  ]);

  const handleConfirmGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      if (isDirectDeposit) {
        const response = await generateAchSplit({
          start_date: periodStart,
          end_date: getEndDateStr(periodStart),
          daily_cap: dailyCap,
          processing_date: processingDate,
          file_id: fileIdInput,
        });
        if (response.data.success) {
          setPreviewOpen(false);
          setPreviewChecks([]);
          setAchPlan(null);
          fetchFiles();
          onAfterGenerate?.();
        }
        return;
      }
      const response = await generatePaymentFile({
        start_date: periodStart,
        end_date: getEndDateStr(periodStart),
        payment_type: paymentType,
      });
      if (response.data.success) {
        setPreviewOpen(false);
        setPreviewChecks([]);
        fetchFiles();
        onAfterGenerate?.();
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          `Failed to generate ${fileLabel.toLowerCase()} file`
      );
    } finally {
      setGenerating(false);
    }
  }, [
    periodStart,
    paymentType,
    fileLabel,
    fetchFiles,
    onAfterGenerate,
    isDirectDeposit,
    dailyCap,
    processingDate,
    fileIdInput,
  ]);

  const handleExport = useCallback(
    async (fileId: number, filename: string) => {
      try {
        const response = await exportPaymentFile(fileId);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            `Failed to export ${fileLabel.toLowerCase()} file`
        );
      }
    },
    [fileLabel]
  );

  const previewColumnDefs = useMemo(
    () =>
      [
        ...(!isDirectDeposit
          ? [
              {
                headerName: "Check #",
                field: "check_number",
                width: 110,
              },
            ]
          : []),
        {
          headerName: "Payee",
          field: "payee",
          flex: 2,
          minWidth: 180,
        },
        {
          headerName: "Amount",
          field: "amount",
          width: 120,
          valueFormatter: (params: any) =>
            params.value != null
              ? `$${Number(params.value).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              : "",
        },
        {
          headerName: "Issue Date",
          field: "issue_date",
          width: 120,
        },
        {
          headerName: "Memo",
          field: "memo",
          flex: 1,
          minWidth: 120,
        },
        {
          headerName: "Type",
          field: "type",
          width: 100,
          cellStyle: (params: any) =>
            params.value === "Void"
              ? { color: "#dc2626", fontWeight: 600 }
              : { color: "#16a34a", fontWeight: 600 },
        },
      ] as ColDef<PaymentPreviewCheck>[],
    [isDirectDeposit]
  );

  return (
    <>
      {error && (
        <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
          <div>{error}</div>
        </div>
      )}

      {loading && (
        <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
          <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
          Loading {fileLabel.toLowerCase()} files...
        </div>
      )}

      {!loading && (
        <div className="tw-w-full">
          <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
            <div className="tw-bg-white tw-p-4">
              <div className="tw-w-full">
                <div
                  className="ag-theme-balham tw-w-full"
                  style={{
                    // Fill the available screen height, but never shrink below
                    // the previous fixed 440px floor on short screens.
                    height: "calc(100vh - 300px)",
                    minHeight: 440,
                  }}
                >
                  <AgGridReact<PaymentFileRow>
                    rowData={rows}
                    defaultColDef={{
                      sortable: true,
                      resizable: true,
                      filter: true,
                    }}
                    columnDefs={
                      [
                        {
                          headerName: "Filename",
                          field: "filename",
                          flex: 2,
                          minWidth: 200,
                        },
                        {
                          headerName: "Account #",
                          field: "account_number",
                          flex: 1,
                          minWidth: 120,
                          valueFormatter: (params: any) => {
                            if (!params.value) return "";
                            const val = String(params.value);
                            const last4 = val.slice(-4);
                            if (isPayrollAdmin) {
                              return "****" + last4;
                            } else {
                              return "********";
                            }
                          },
                        },
                        ...(isDirectDeposit
                          ? ([
                              {
                                headerName: "File ID",
                                field: "file_id",
                                width: 100,
                              },
                              {
                                headerName: "Delivery Date",
                                field: "delivery_date",
                                width: 130,
                              },
                            ] as ColDef<PaymentFileRow>[])
                          : []),
                        {
                          headerName: "Items",
                          field: "items_count",
                          flex: 1,
                          minWidth: 80,
                        },
                        {
                          headerName: "Total Amount",
                          field: "total_amount",
                          flex: 1,
                          minWidth: 120,
                          valueFormatter: (params: any) =>
                            params.value != null
                              ? `$${Number(params.value).toLocaleString(
                                  "en-US",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}`
                              : "",
                        },
                        {
                          headerName: "Created By",
                          field: "created_by",
                          flex: 1,
                          minWidth: 120,
                        },
                        {
                          headerName: "Created At",
                          field: "created_at",
                          flex: 1,
                          minWidth: 150,
                          valueFormatter: (params: any) =>
                            params.value
                              ? new Date(params.value).toLocaleString()
                              : "",
                        },
                        {
                          headerName: "Export",
                          field: "id",
                          flex: 1,
                          minWidth: 100,
                          sortable: false,
                          filter: false,
                          cellRenderer: (params: any) => (
                            <Button
                              size="small"
                              startIcon={
                                !isPayrollAdmin ? (
                                  <FileDownloadOffIcon />
                                ) : (
                                  <FileDownloadIcon />
                                )
                              }
                              onClick={() =>
                                handleExport(
                                  params.data.id,
                                  params.data.filename
                                )
                              }
                              disabled={!isPayrollAdmin}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&.Mui-disabled": {
                                  cursor: "not-allowed",
                                  pointerEvents: "auto",
                                },
                              }}
                            >
                              Export
                            </Button>
                          ),
                        },
                      ] as ColDef<PaymentFileRow>[]
                    }
                  />
                </div>
                <div className="tw-border-l tw-border-r tw-border-b tw-border-gray-300 tw-p-1 tw-bg-white tw-flex tw-gap-2">
                  <Button
                    color="primary"
                    sx={{
                      bgcolor: "#2642c9",
                      textTransform: "none",
                      "&.Mui-disabled": {
                        cursor: "not-allowed",
                        pointerEvents: "auto",
                      },
                    }}
                    variant="contained"
                    startIcon={<NoteAddIcon />}
                    onClick={handleGenerateClick}
                    disabled={generating || previewLoading || !isPayrollAdmin}
                  >
                    {previewLoading ? "Loading..." : "Generate File"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Generate Confirmation Modal */}
      <Dialog
        open={previewOpen}
        onClose={() => {
          if (!generating) {
            setPreviewOpen(false);
            setPreviewChecks([]);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Confirm {fileLabel} File Generation
          <IconButton
            onClick={() => {
              setPreviewOpen(false);
              setPreviewChecks([]);
            }}
            size="small"
            disabled={generating}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isDirectDeposit ? (
            <>
              <p className="tw-mb-3 tw-text-[13px] tw-text-gray-700">
                <strong>
                  {achPlan?.eligible_count ?? previewChecks.length}
                </strong>{" "}
                eligible direct deposit(s) will be split into one ACH file per
                processing day, each capped at the daily limit. Files settle the
                next business day; no file is scheduled past Thursday.
              </p>
              <div className="tw-flex tw-gap-4 tw-mb-4 tw-items-end tw-flex-wrap">
                <div className="tw-flex tw-flex-col">
                  <label className="tw-text-[12px] tw-text-gray-600 tw-mb-1">
                    First Processing Date (Mon–Thu)
                  </label>
                  <input
                    type="date"
                    value={processingDate}
                    onChange={(e) => {
                      setProcessingDate(e.target.value);
                      loadAchPlan(e.target.value, dailyCap, fileIdInput);
                    }}
                    disabled={generating}
                    className="tw-border tw-rounded tw-px-2 tw-py-1 tw-text-[13px] tw-bg-white"
                  />
                </div>
                <div className="tw-flex tw-flex-col">
                  <label className="tw-text-[12px] tw-text-gray-600 tw-mb-1">
                    Daily Cap ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    value={dailyCap}
                    onChange={(e) => {
                      setDailyCap(e.target.value);
                      loadAchPlan(processingDate, e.target.value, fileIdInput);
                    }}
                    disabled={generating}
                    className="tw-border tw-rounded tw-px-2 tw-py-1 tw-text-[13px] tw-bg-white tw-w-32"
                  />
                </div>
                <div className="tw-flex tw-flex-col">
                  <label className="tw-text-[12px] tw-text-gray-600 tw-mb-1">
                    File ID (A-Z, 0-9)
                  </label>
                  <input
                    type="text"
                    value={fileIdInput}
                    onChange={(e) => {
                      const v = e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z0-9]/g, "")
                        .slice(0, 10);
                      setFileIdInput(v);
                      loadAchPlan(processingDate, dailyCap, v);
                    }}
                    disabled={generating}
                    placeholder="A"
                    className="tw-border tw-rounded tw-px-2 tw-py-1 tw-text-[13px] tw-bg-white tw-uppercase tw-w-24 tw-font-mono"
                  />
                </div>
              </div>

              {achPlanLoading && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-gray-600 tw-py-6 tw-justify-center">
                  <Spinner className="tw-w-4 tw-h-4" />
                  Calculating split…
                </div>
              )}

              {!achPlanLoading && achPlan && !achPlan.feasible && (
                <div className="tw-border tw-border-red-300 tw-bg-red-50 tw-rounded tw-p-3 tw-mb-2">
                  <div className="tw-text-red-700 tw-font-semibold tw-mb-1">
                    Cannot schedule all payments by Thursday
                  </div>
                  <div className="tw-text-red-700 tw-text-[13px] tw-mb-2">
                    {achPlan.reason}
                  </div>
                  {achPlan.overflow.length > 0 && (
                    <>
                      <div className="tw-text-[12px] tw-font-semibold tw-text-gray-700">
                        Unscheduled ({achPlan.overflow.length}):
                      </div>
                      <ul className="tw-list-disc tw-pl-5 tw-text-[13px]">
                        {achPlan.overflow.map((p) => (
                          <li key={p.id}>
                            {p.payee} —{" "}
                            {p.amount.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {!achPlanLoading && achPlan && achPlan.feasible && (
                <div className="tw-flex tw-flex-col tw-gap-3">
                  {achPlan.days.map((d) => (
                    <div
                      key={d.day}
                      className="tw-border tw-border-gray-200 tw-rounded tw-overflow-hidden"
                    >
                      <div className="tw-flex tw-justify-between tw-items-center tw-bg-gray-50 tw-px-3 tw-py-2 tw-border-b tw-border-gray-200">
                        <div className="tw-text-[13px] tw-font-semibold tw-text-gray-800">
                          Day {d.day} · File {d.file_id}
                          <span className="tw-font-normal tw-text-gray-600">
                            {" "}
                            — process {d.processing_date}, deliver{" "}
                            {d.delivery_date}
                          </span>
                        </div>
                        <div className="tw-text-[13px] tw-text-gray-700">
                          {d.items_count} item(s) ·{" "}
                          <strong>
                            {d.total_amount.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })}
                          </strong>{" "}
                          / cap{" "}
                          {achPlan.daily_cap.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </div>
                      </div>
                      <ul className="tw-px-3 tw-py-2 tw-text-[13px] tw-max-h-40 tw-overflow-auto">
                        {d.payments.map((p) => (
                          <li
                            key={p.id}
                            className="tw-flex tw-justify-between tw-py-0.5"
                          >
                            <span>{p.payee}</span>
                            <span className="tw-font-mono">
                              {p.amount.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                              })}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <p className="tw-mb-3">
                The following <strong>{previewChecks.length}</strong> {itemNoun}
                (s) will be included in the {fileLabel.toLowerCase()} file.
                Total:{" "}
                <strong>
                  $
                  {previewChecks
                    .reduce((sum, c) => sum + c.amount, 0)
                    .toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </strong>
              </p>
              <div
                className="ag-theme-balham tw-w-full"
                style={{
                  height: Math.min(previewChecks.length * 28 + 56, 400),
                }}
              >
                <AgGridReact<PaymentPreviewCheck>
                  rowData={previewChecks}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                  }}
                  columnDefs={previewColumnDefs}
                />
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setPreviewOpen(false);
              setPreviewChecks([]);
              setAchPlan(null);
            }}
            disabled={generating}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ bgcolor: "#2642c9", textTransform: "none" }}
            onClick={handleConfirmGenerate}
            disabled={
              generating ||
              (isDirectDeposit &&
                (achPlanLoading || !achPlan || !achPlan.feasible))
            }
            startIcon={
              generating ? (
                <Spinner className="tw-w-4 tw-h-4" />
              ) : (
                <NoteAddIcon />
              )
            }
          >
            {generating
              ? "Generating..."
              : isDirectDeposit && achPlan?.feasible
              ? `Confirm — Generate ${achPlan.days.length} File(s)`
              : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export function PayRollReviewPage(): JSX.Element {
  const [periodStart, setPeriodStart] = useState<string>(getPreviousSunday());
  const [rows, setRows] = useState<PayrollReviewRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getIsPayrollAdmin } = useAuth();
  const [isPayrollAdmin, setIsPayrollAdmin] = useState(false);
  // Checks tab state
  const [checksRows, setChecksRows] = useState<ChecksReviewRow[]>([]);
  const [checksLoading, setChecksLoading] = useState(false);
  const [checksError, setChecksError] = useState<string | null>(null);

  // Direct Deposits tab state
  const [directDepositRows, setDirectDepositRows] = useState<ChecksReviewRow[]>(
    []
  );
  const [directDepositLoading, setDirectDepositLoading] = useState(false);
  const [directDepositError, setDirectDepositError] = useState<string | null>(
    null
  );

  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  const [paymentsSubTab, setPaymentsSubTab] = useState(0);
  const handlePaymentsSubTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setPaymentsSubTab(newValue);
  };
  const [filesSubTab, setFilesSubTab] = useState(0);
  const handleFilesSubTabChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setFilesSubTab(newValue);
  };

  // Filter state
  const [searchVal, setSearchVal] = useState("");
  const [payTypeFilter, setPayTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [checksStatusFilter, setChecksStatusFilter] = useState("");
  const [directDepositStatusFilter, setDirectDepositStatusFilter] =
    useState("");

  const fetchReviewData = useCallback(async (startStr: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosClient.post("/payroll/review", {
        start_date: startStr,
        end_date: getEndDateStr(startStr),
      });
      setRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(
        err?.response?.data?.message || "Failed to fetch payroll review data"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchChecksData = useCallback(async (startStr: string) => {
    setChecksLoading(true);
    setChecksError(null);
    try {
      const response = await fetchChecksReview({
        start_date: startStr,
        end_date: getEndDateStr(startStr),
        payment_type: "check",
      });
      setChecksRows(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setChecksError(
        err?.response?.data?.message || "Failed to fetch checks data"
      );
    } finally {
      setChecksLoading(false);
    }
  }, []);

  const fetchDirectDepositData = useCallback(async (startStr: string) => {
    setDirectDepositLoading(true);
    setDirectDepositError(null);
    try {
      const response = await fetchChecksReview({
        start_date: startStr,
        end_date: getEndDateStr(startStr),
        payment_type: "direct_deposit",
      });
      const mapped: ChecksReviewRow[] = (
        Array.isArray(response.data) ? (response.data as ChecksReviewRow[]) : []
      ).map((r) => ({
        ...r,
        Status:
          r.Status === "Check Issued"
            ? "Direct Generated"
            : r.Status === "Check Cancelled"
            ? "Direct Deposit Canceled"
            : r.Status,
      }));
      setDirectDepositRows(mapped);
    } catch (err: any) {
      setDirectDepositError(
        err?.response?.data?.message || "Failed to fetch direct deposit data"
      );
    } finally {
      setDirectDepositLoading(false);
    }
  }, []);

  useEffect(() => {
    async function fetchInfo() {
      const result = await getIsPayrollAdmin();
      if (!result) {
        setIsPayrollAdmin(false);
        return;
      }
      setIsPayrollAdmin(result);
    }
    fetchInfo();
  }, [getIsPayrollAdmin]);

  useEffect(() => {
    fetchReviewData(periodStart);
    fetchChecksData(periodStart);
    fetchDirectDepositData(periodStart);
  }, [periodStart, fetchReviewData, fetchChecksData, fetchDirectDepositData]);

  // useEffect(() => {
  //   if (!isPayrollAdmin && activeTab === 2) {
  //     setActiveTab(0);
  //   }
  // }, [isPayrollAdmin, activeTab]);

  // Apply filters to rows
  const filteredRows = useMemo(() => {
    let result = rows;

    if (searchVal) {
      const search = searchVal.toLowerCase();
      result = result.filter(
        (row) =>
          String(row["B&B ID"] ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Payee ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Memo ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row["Check Number"] ?? "")
            .toLowerCase()
            .includes(search)
      );
    }

    if (payTypeFilter) {
      result = result.filter((row) => row["Pay Type"] === payTypeFilter);
    }

    if (statusFilter) {
      result = result.filter((row) => row.Status === statusFilter);
    }

    return result;
  }, [rows, searchVal, payTypeFilter, statusFilter]);

  // Group filtered rows by Memo (plant name), ordered by Plant ID
  const plantGroups = useMemo(() => {
    const groups: Record<
      string,
      { plantId: number; rows: PayrollReviewRow[] }
    > = {};
    filteredRows.forEach((row) => {
      const plant = row.Memo ?? "Unknown";
      if (!groups[plant])
        groups[plant] = {
          plantId: (row as any)["Plant ID"] ?? Infinity,
          rows: [],
        };
      groups[plant].rows.push(row);
    });
    return Object.entries(groups)
      .sort(([, a], [, b]) => a.plantId - b.plantId)
      .map(
        ([plantName, { rows }]) =>
          [plantName, rows] as [string, PayrollReviewRow[]]
      );
  }, [filteredRows]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "B&B ID",
        headerName: "B&B ID",
        width: 120,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
      },
      { field: "Pay Type", headerName: "Pay Type", width: 100 },
      // { field: "Date", headerName: "Date", width: 110 },
      { field: "Payee", headerName: "Payee", width: 300 },
      // { field: "Memo", headerName: "Memo", width: 140 },
      {
        field: "Amount",
        headerName: "Amount",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Gross Pay",
        headerName: "Gross Pay",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      { field: "Check Number", headerName: "Check #", width: 100 },
      {
        field: "Tax",
        headerName: "Tax",
        width: 80,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Hours",
        headerName: "Hours",
        width: 80,
        valueFormatter: decimalFormatter,
      },
      {
        field: "Pay Rate Value",
        headerName: "Pay Rate",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Rate Type",
        headerName: "Rate Type",
        width: 100,
        valueFormatter: (params) =>
          params.value
            ? String(params.value).charAt(0).toUpperCase() +
              String(params.value).slice(1)
            : "",
      },
      {
        field: "Overtime Rate",
        headerName: "OT Rate",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Other",
        headerName: "Other",
        width: 80,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Other Additions",
        headerName: "Other Add.",
        width: 110,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Admin Fee",
        headerName: "Admin Fee",
        width: 100,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Rent",
        headerName: "Rent",
        width: 80,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Holiday",
        headerName: "Holiday",
        width: 90,
        valueFormatter: currencyFormatter,
      },
      {
        field: "PTO",
        headerName: "PTO",
        width: 80,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Status",
        headerName: "Status",
        width: 80,
        pinned: "right",
        cellRenderer: StatusCellRenderer,
      },
      { field: "Pay Period", headerName: "Pay Period", width: 170 },
    ],
    []
  );

  // Checks tab: filtered rows
  const filteredChecksRows = useMemo(() => {
    let result = checksRows;
    if (searchVal) {
      const search = searchVal.toLowerCase();
      result = result.filter(
        (row) =>
          String(row["B&B ID"] ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Payee ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Memo ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row["Check Number"] ?? "")
            .toLowerCase()
            .includes(search)
      );
    }
    if (checksStatusFilter) {
      result = result.filter((row) => row.Status === checksStatusFilter);
    }
    return result;
  }, [checksRows, searchVal, checksStatusFilter]);

  // Direct Deposits tab: filtered rows
  const filteredDirectDepositRows = useMemo(() => {
    let result = directDepositRows;
    if (searchVal) {
      const search = searchVal.toLowerCase();
      result = result.filter(
        (row) =>
          String(row["B&B ID"] ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Payee ?? "")
            .toLowerCase()
            .includes(search) ||
          String(row.Memo ?? "")
            .toLowerCase()
            .includes(search)
      );
    }
    if (directDepositStatusFilter) {
      result = result.filter((row) => row.Status === directDepositStatusFilter);
    }
    return result;
  }, [directDepositRows, searchVal, directDepositStatusFilter]);

  // Checks tab: column definitions
  const checksColumnDefs: ColDef[] = useMemo(
    () => [
      // {
      //   field: "B&B ID",
      //   headerName: "B&B ID",
      //   width: 120,
      //   checkboxSelection: true,
      //   headerCheckboxSelection: true,
      // },
      {
        field: "Check Number",
        headerName: "Check #",
        width: 120,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellStyle: (params: any) =>
          params.data?.Status === "Check Cancelled"
            ? { color: "#d32f2f", fontWeight: 600 }
            : null,
      },
      { field: "Memo", headerName: "Plant", width: 160 },
      {
        field: "B&B ID",
        headerName: "B&B ID",
        width: 100,
      },
      { field: "Payee", headerName: "Payee", width: 300 },
      {
        field: "Amount",
        headerName: "Amount",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Gross Pay",
        headerName: "Gross Pay",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Tax",
        headerName: "Tax",
        width: 100,
        valueFormatter: currencyFormatter,
      },

      { field: "Issue Date", headerName: "Issue Date", width: 120 },
      { field: "Print Count", headerName: "Print #", width: 90 },
      { field: "Pay Period", headerName: "Pay Period", width: 170 },
      {
        field: "Cancel Reason",
        headerName: "Cancel Reason",
        width: 250,
        cellStyle: (params: any) =>
          params.data?.Status === "Check Cancelled"
            ? { fontStyle: "italic", color: "#666" }
            : null,
      },
      {
        field: "Payment File",
        headerName: "PP File",
        width: 200,
      },
      {
        field: "Payment Cancel File",
        headerName: "PP Cancel File",
        width: 200,
      },
      {
        field: "Status",
        headerName: "Status",
        width: 120,
        pinned: "right",
        cellRenderer: CheckStatusCellRenderer,
      },
    ],
    []
  );

  // Direct Deposits tab: column definitions (no Check Number, no Print Count)
  const directDepositColumnDefs: ColDef[] = useMemo(
    () => [
      {
        field: "B&B ID",
        headerName: "B&B ID",
        width: 120,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        cellStyle: (params: any) =>
          params.data?.Status === "Direct Deposit Canceled"
            ? { color: "#d32f2f", fontWeight: 600 }
            : null,
      },
      { field: "Memo", headerName: "Plant", width: 160 },
      { field: "Payee", headerName: "Payee", width: 300 },
      {
        field: "Amount",
        headerName: "Amount",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Gross Pay",
        headerName: "Gross Pay",
        width: 130,
        valueFormatter: currencyFormatter,
      },
      {
        field: "Tax",
        headerName: "Tax",
        width: 100,
        valueFormatter: currencyFormatter,
      },
      { field: "Issue Date", headerName: "Issue Date", width: 120 },
      { field: "Pay Period", headerName: "Pay Period", width: 170 },
      {
        field: "Cancel Reason",
        headerName: "Cancel Reason",
        width: 250,
        cellStyle: (params: any) =>
          params.data?.Status === "Direct Deposit Canceled"
            ? { fontStyle: "italic", color: "#666" }
            : null,
      },
      {
        field: "Payment File",
        headerName: "DD File",
        width: 200,
      },
      {
        field: "Status",
        headerName: "Status",
        width: 160,
        pinned: "right",
        cellRenderer: CheckStatusCellRenderer,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      // Floor so sizeColumnsToFit() keeps columns readable on narrow monitors
      // (a horizontal scrollbar appears) while letting them stretch on wide ones.
      minWidth: 80,
    }),
    []
  );

  return (
    <div className="tw-p-6 tw-w-full tw-mx-auto">
      <div className="tw-w-full tw-flex tw-flex-col tw-justify-center">
        {error && (
          <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
            <div>{error}</div>
          </div>
        )}

        <PayrollReviewFilters
          searchVal={searchVal}
          onSearchChange={setSearchVal}
          payTypeFilter={payTypeFilter}
          onPayTypeChange={setPayTypeFilter}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          checksStatusFilter={checksStatusFilter}
          onChecksStatusChange={setChecksStatusFilter}
          directDepositStatusFilter={directDepositStatusFilter}
          onDirectDepositStatusChange={setDirectDepositStatusFilter}
          activeTab={activeTab}
          paymentsSubTab={paymentsSubTab}
          periodStart={periodStart}
          onPeriodStartChange={setPeriodStart}
        />

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="payroll review tabs"
          sx={{
            "& .MuiTabs-indicator": {
              display: "none",
            },
            minHeight: "20px",
          }}
        >
          <Tab
            label="Payroll Review"
            sx={{
              backgroundColor:
                activeTab === 0 ? "#2642c9 !important" : "white !important",
              color: activeTab === 0 ? "#fff !important" : "#000 !important",
              borderRadius: "6px 0px 0px 0px",
              minHeight: "30px !important",
              fontSize: "0.875rem",
              padding: "4px 12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  activeTab === 0 ? "#2642c9 !important" : "#d0d0d0 !important",
              },
            }}
          />
          <Tab
            label="Payments"
            sx={{
              backgroundColor:
                activeTab === 1 ? "#2642c9 !important" : "white !important",
              color: activeTab === 1 ? "#fff !important" : "#000 !important",
              minHeight: "30px !important",
              fontSize: "0.875rem",
              padding: "4px 12px",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  activeTab === 1 ? "#2642c9 !important" : "#d0d0d0 !important",
              },
            }}
          />
          {
            <Tab
              label="Bank Files"
              sx={{
                backgroundColor:
                  activeTab === 2 ? "#2642c9 !important" : "white !important",
                color: activeTab === 2 ? "#fff !important" : "#000 !important",
                borderRadius: "0px 6px 0px 0px",
                minHeight: "30px !important",
                fontSize: "0.875rem",
                padding: "4px 12px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    activeTab === 2
                      ? "#2642c9 !important"
                      : "#d0d0d0 !important",
                },
              }}
            />
          }
        </Tabs>

        {/* Tab 0: Payroll Review */}
        <div role="tabpanel" hidden={activeTab !== 0}>
          {isLoading && (
            <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
              <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
              Loading data...
            </div>
          )}

          {!isLoading && rows.length === 0 && (
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-text-gray-400">
              <SearchOffIcon sx={{ fontSize: 64 }} />
              <span className="tw-mt-2 tw-text-sm">
                No payroll data found for this period
              </span>
            </div>
          )}

          {!isLoading && rows.length > 0 && (
            <div className="tw-w-full">
              <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
                {plantGroups.map(([plantName, plantRows]) => (
                  <div className="tw-bg-white tw-p-4 tw-mb-3" key={plantName}>
                    <PlantPayrollGrid
                      plantName={plantName}
                      rows={plantRows}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      periodStart={periodStart}
                      onFinalized={() => {
                        fetchReviewData(periodStart);
                        fetchChecksData(periodStart);
                        fetchDirectDepositData(periodStart);
                      }}
                      onRowsStatusChange={(bbIds, status) => {
                        // Flip the affected rows' status in place instead of
                        // refetching the review grid; the Payments tab still
                        // needs the newly generated / voided checks and
                        // direct deposits.
                        const ids = new Set(bbIds.map(String));
                        setRows((prev) =>
                          prev.map((r) =>
                            ids.has(String(r["B&B ID"]))
                              ? { ...r, Status: status }
                              : r
                          )
                        );
                        fetchChecksData(periodStart);
                        fetchDirectDepositData(periodStart);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tab 1: Payments (Checks + Direct Deposits) */}
        <div role="tabpanel" hidden={activeTab !== 1}>
          <Tabs
            value={paymentsSubTab}
            onChange={handlePaymentsSubTabChange}
            aria-label="payments sub tabs"
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              minHeight: "20px",
              marginTop: "8px",
            }}
          >
            <Tab
              label="Checks"
              sx={{
                backgroundColor:
                  paymentsSubTab === 0
                    ? "#2642c9 !important"
                    : "white !important",
                color:
                  paymentsSubTab === 0 ? "#fff !important" : "#000 !important",
                borderRadius: "6px 0px 0px 0px",
                minHeight: "30px !important",
                fontSize: "0.875rem",
                padding: "4px 12px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    paymentsSubTab === 0
                      ? "#2642c9 !important"
                      : "#d0d0d0 !important",
                },
              }}
            />
            <Tab
              label="Direct Deposits"
              sx={{
                backgroundColor:
                  paymentsSubTab === 1
                    ? "#2642c9 !important"
                    : "white !important",
                color:
                  paymentsSubTab === 1 ? "#fff !important" : "#000 !important",
                borderRadius: "0px 6px 0px 0px",
                minHeight: "30px !important",
                fontSize: "0.875rem",
                padding: "4px 12px",
                textTransform: "none",
                "&:hover": {
                  backgroundColor:
                    paymentsSubTab === 1
                      ? "#2642c9 !important"
                      : "#d0d0d0 !important",
                },
              }}
            />
          </Tabs>

          {/* Sub-tab 0: Checks */}
          <div role="tabpanel" hidden={paymentsSubTab !== 0}>
            {checksError && (
              <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
                <div>{checksError}</div>
              </div>
            )}

            {checksLoading && (
              <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
                <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
                Loading checks data...
              </div>
            )}

            {!checksLoading && (
              <div className="tw-w-full">
                <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
                  <div className="tw-bg-white tw-p-4">
                    <PlantChecksGrid
                      plantName="Checks"
                      rows={filteredChecksRows}
                      columnDefs={checksColumnDefs}
                      defaultColDef={defaultColDef}
                      periodStart={periodStart}
                      onRefresh={() => {
                        fetchChecksData(periodStart);
                        fetchReviewData(periodStart);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sub-tab 1: Direct Deposits */}
          <div role="tabpanel" hidden={paymentsSubTab !== 1}>
            {directDepositError && (
              <div className="tw-text-center tw-py-4 tw-text-red-600 tw-flex tw-justify-center">
                <div>{directDepositError}</div>
              </div>
            )}

            {directDepositLoading && (
              <div className="tw-flex tw-flex-col tw-justify-center tw-items-center tw-min-w-[580px] tw-min-h-[55vh]">
                <Spinner className="tw-flex tw-justify-center tw-ml-2 tw-items-center tw-w-full" />
                Loading direct deposits data...
              </div>
            )}

            {!directDepositLoading && (
              <div className="tw-w-full">
                <div className="tw-rounded-r-lg tw-border tw-border-gray-200">
                  <div className="tw-bg-white tw-p-4">
                    <PlantChecksGrid
                      plantName="Direct Deposits"
                      rows={filteredDirectDepositRows}
                      columnDefs={directDepositColumnDefs}
                      defaultColDef={defaultColDef}
                      periodStart={periodStart}
                      paymentType="direct_deposit"
                      onRefresh={() => {
                        fetchDirectDepositData(periodStart);
                        fetchReviewData(periodStart);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tab 2: Files (Positive Checks + ACH Files) */}
        {
          <div role="tabpanel" hidden={activeTab !== 2}>
            <Tabs
              value={filesSubTab}
              onChange={handleFilesSubTabChange}
              aria-label="payment files sub tabs"
              sx={{
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                minHeight: "20px",
                marginTop: "8px",
              }}
            >
              <Tab
                label="Positive Checks"
                sx={{
                  backgroundColor:
                    filesSubTab === 0
                      ? "#2642c9 !important"
                      : "white !important",
                  color:
                    filesSubTab === 0 ? "#fff !important" : "#000 !important",
                  borderRadius: "6px 0px 0px 0px",
                  minHeight: "30px !important",
                  fontSize: "0.875rem",
                  padding: "4px 12px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      filesSubTab === 0
                        ? "#2642c9 !important"
                        : "#d0d0d0 !important",
                  },
                }}
              />
              <Tab
                label="ACH"
                sx={{
                  backgroundColor:
                    filesSubTab === 1
                      ? "#2642c9 !important"
                      : "white !important",
                  color:
                    filesSubTab === 1 ? "#fff !important" : "#000 !important",
                  borderRadius: "0px 6px 0px 0px",
                  minHeight: "30px !important",
                  fontSize: "0.875rem",
                  padding: "4px 12px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor:
                      filesSubTab === 1
                        ? "#2642c9 !important"
                        : "#d0d0d0 !important",
                  },
                }}
              />
            </Tabs>

            <div role="tabpanel" hidden={filesSubTab !== 0}>
              <PaymentFilesTab
                paymentType="check"
                periodStart={periodStart}
                onAfterGenerate={() => fetchChecksData(periodStart)}
                isPayrollAdmin={isPayrollAdmin}
              />
            </div>

            <div role="tabpanel" hidden={filesSubTab !== 1}>
              <PaymentFilesTab
                paymentType="direct_deposit"
                periodStart={periodStart}
                onAfterGenerate={() => fetchDirectDepositData(periodStart)}
                isPayrollAdmin={isPayrollAdmin}
              />
            </div>
          </div>
        }
      </div>
    </div>
  );
}

export default PayRollReviewPage;
